/* ==========================================================================
   VoicePad: the fully customizable AI gadget
   Behaviour for templates/page.voicepad-gadget.json.
     - glass-cube field (hero, sign-up)
     - hero: load sequence, live keys, tilt and metal highlight
     - key chapter: film frames scrubbed by scroll, job cubes flying into keys
     - a day of scenes: clock, pad and words as said, cleaned up and landed
     - your AI and Genspark demos
     - headline word reveal, top bar tone and progress, pointer label
   Everything runs only while it is on screen.
   ========================================================================== */
(function () {
  'use strict';

  const doc = document;
  const root = doc.documentElement;
  root.classList.add('vpg-js');
  window.__vpgReady = true;

  const mq = (q) => !!(window.matchMedia && matchMedia(q).matches);
  const REDUCE = mq('(prefers-reduced-motion: reduce)');
  const FINE = mq('(hover: hover) and (pointer: fine)');
  const DESIGN = !!(window.Shopify && window.Shopify.designMode);
  const STOP = { stop: true };

  /* ------------------------------------------------------------- utils */
  const $ = (s, el) => (el || doc).querySelector(s);
  const $$ = (s, el) => Array.prototype.slice.call((el || doc).querySelectorAll(s));
  const clamp = (v, a, b) => (v < a ? a : v > b ? b : v);
  const lerp = (a, b, t) => a + (b - a) * t;
  const smooth = (t) => { t = clamp(t, 0, 1); return t * t * (3 - 2 * t); };
  const easeIO = (t) => (t < .5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
  const hash = (x, y) => { const s = Math.sin(x * 127.1 + y * 311.7) * 43758.5453; return s - Math.floor(s); };
  const swallow = (e) => { if (e !== STOP && window.console) console.error(e); };
  const inView = (el) => { const r = el.getBoundingClientRect(); return r.bottom > 0 && r.top < (window.innerHeight || 0); };

  function onScreen(el, enter, leave, threshold, margin) {
    if (!('IntersectionObserver' in window)) { enter(); return null; }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) enter(); else if (leave) leave(); });
    }, { threshold: threshold == null ? 0.2 : threshold, rootMargin: margin || '0px' });
    io.observe(el);
    return io;
  }

  /* one requestAnimationFrame loop for everything that moves with time */
  const Ticker = (() => {
    const fns = new Set();
    let raf = 0, last = 0;
    function loop(now) {
      raf = 0;
      const dt = Math.min(64, now - (last || now));
      last = now;
      fns.forEach((f) => { try { f(now, dt); } catch (e) { swallow(e); fns.delete(f); } });
      if (fns.size) raf = requestAnimationFrame(loop); else last = 0;
    }
    return {
      add(f) { fns.add(f); if (!raf) raf = requestAnimationFrame(loop); },
      remove(f) { fns.delete(f); }
    };
  })();

  /* cancellable async scripts */
  class Runner {
    constructor() { this.gen = 0; }
    token() { const g = ++this.gen; return { alive: () => this.gen === g }; }
    stop() { this.gen++; }
  }
  const wait = (tok, ms) => new Promise((res, rej) => setTimeout(() => (tok.alive() ? res() : rej(STOP)), ms));
  async function typeText(tok, el, text, speed) {
    for (let i = 1; i <= text.length; i++) {
      el.textContent = text.slice(0, i);
      const ch = text.charAt(i - 1);
      await wait(tok, speed + (ch === ' ' ? speed * .5 : 0) + (/[.,?!]/.test(ch) ? speed * 4 : 0));
    }
  }

  /* a demo that plays once per call and reports when it is done */
  function Scripted(reset, script, duration) {
    const run = new Runner();
    reset();
    return {
      duration,
      run,
      play(done) {
        run.stop(); reset();
        const tok = run.token();
        (async () => { try { await script(tok); if (done) done(); } catch (e) { swallow(e); } })();
      },
      stop() { run.stop(); reset(); }
    };
  }
  /* a demo that loops while it is on screen */
  function Looping(el, reset, script, still) {
    const d = Scripted(reset, script, 0);
    let on = false;
    function loop() { if (!on) return; d.play(() => { if (on) loop(); }); }
    const api = {
      play() { if (on) return; on = true; loop(); },
      pause() { on = false; d.stop(); },
      restart() { on = false; d.stop(); on = true; loop(); },
      destroy() { if (api.io) api.io.disconnect(); api.pause(); }
    };
    if (REDUCE) { if (still) still(); return api; }
    api.io = onScreen(el, api.play, api.pause, .35);
    return api;
  }

  /* ---------------------------------------------- headline word reveal */
  function splitWords(el) {
    if (el.classList.contains('is-split')) return;
    const label = Array.prototype.map.call(el.childNodes, (n) => (n.nodeName === 'BR' ? ' ' : n.textContent)).join('').replace(/\s+/g, ' ').trim();
    const wrap = doc.createElement('span');
    wrap.setAttribute('aria-hidden', 'true');
    let i = 0;
    const word = (node) => {
      const outer = doc.createElement('span');
      outer.className = 'vpg-w';
      const inner = doc.createElement('span');
      inner.style.setProperty('--i', i++);
      inner.appendChild(node);
      outer.appendChild(inner);
      return outer;
    };
    Array.prototype.slice.call(el.childNodes).forEach((node) => {
      if (node.nodeType === 3) {
        node.textContent.split(/(\s+)/).forEach((part) => {
          if (!part) return;
          if (/^\s+$/.test(part)) wrap.appendChild(doc.createTextNode(' '));
          else wrap.appendChild(word(doc.createTextNode(part)));
        });
      } else if (node.nodeType === 1) {
        if (node.nodeName === 'BR') wrap.appendChild(doc.createElement('br'));
        else wrap.appendChild(word(node.cloneNode(true)));
      }
    });
    el.textContent = '';
    el.appendChild(wrap);
    el.setAttribute('aria-label', label);
    el.classList.add('is-split');
  }
  function reveals(scope) {
    const els = $$('[data-split]', scope);
    if (REDUCE) return;
    els.forEach((el) => {
      splitWords(el);
      const delay = parseInt(el.getAttribute('data-split-delay') || '0', 10);
      el.style.setProperty('--d0', delay + 'ms');
      if (DESIGN) { el.classList.add('is-in'); return; }
      if (el.getAttribute('data-split') === 'load') {
        requestAnimationFrame(() => setTimeout(() => el.classList.add('is-in'), 120));
        return;
      }
      if (!('IntersectionObserver' in window)) { el.classList.add('is-in'); return; }
      const io = new IntersectionObserver((en) => {
        en.forEach((e) => { if (e.isIntersecting) { el.classList.add('is-in'); io.disconnect(); } });
      }, { threshold: .25, rootMargin: '0px 0px -8% 0px' });
      io.observe(el);
    });
    $$('[data-vpg-mark]', scope).forEach((el) => {
      if (REDUCE || DESIGN) { el.classList.add('is-in'); return; }
      onScreen(el, () => el.classList.add('is-in'), null, .2);
    });
  }

  /* ------------------------------------------------------ device keys */
  function setLegend(key, text, animate) {
    const el = $('[data-legend]', key);
    if (!el || el.textContent === text) return;
    el.textContent = text;
    if (animate && !REDUCE) {
      key.classList.remove('is-flip');
      void key.offsetWidth;
      key.classList.add('is-flip');
    }
  }
  function hit(key) {
    if (!key) return;
    key.classList.add('is-hit');
    clearTimeout(key._hit);
    key._hit = setTimeout(() => key.classList.remove('is-hit'), 420);
  }

  /* ================================================== glass-cube field */
  const ICON_PATHS = [
    '<rect x="9" y="3" width="6" height="11" rx="3"/><path d="M5.5 11a6.5 6.5 0 0 0 13 0M12 17.5V21"/>',
    '<path d="M20 11.5a8 8 0 0 1-11.7 7.1L4 19.8l1.2-4A8 8 0 1 1 20 11.5z"/>',
    '<path d="M6 3h8.5L19 7.5V21H6z"/><path d="M14 3v5h5M9 12h6M9 16h6"/>',
    '<rect x="3" y="4" width="18" height="16" rx="2"/><path d="M3 9.5h18M3 14.75h18M9 4v16"/>',
    '<rect x="3" y="4" width="18" height="12" rx="2"/><path d="M8.5 20h7M12 16v4"/>',
    '<circle cx="12" cy="12" r="9"/><path d="M3 12h18M12 3c2.4 2.6 3.6 5.6 3.6 9s-1.2 6.4-3.6 9c-2.4-2.6-3.6-5.6-3.6-9S9.6 5.6 12 3z"/>',
    '<circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="3.4" fill="#fff" stroke="none"/>',
    '<rect x="3" y="4" width="18" height="16" rx="3"/><path d="M7.5 9.5l3 2.5-3 2.5M12.5 15h4"/>',
    '<path d="M4 20h4L19 9l-4-4L4 16z"/><path d="M13.5 6.5l4 4"/>',
    '<path d="M4 15v-3a8 8 0 0 1 16 0v3"/><rect x="3" y="14" width="4" height="6.5" rx="1.6"/><rect x="17" y="14" width="4" height="6.5" rx="1.6"/>',
    '<path d="M4 10v4M8 7v10M12 4v16M16 8v8M20 10v4"/>',
    '<circle cx="9" cy="8" r="3.5"/><path d="M2.5 20c.6-3.4 3.2-5.5 6.5-5.5s5.9 2.1 6.5 5.5"/><path d="M16 4.6a3.5 3.5 0 0 1 0 6.8M18.6 14.9c1.6.8 2.7 2.6 3 5.1"/>'
  ];
  let ICON_IMGS = null;
  function iconImages() {
    if (ICON_IMGS) return ICON_IMGS;
    ICON_IMGS = ICON_PATHS.map((p) => {
      const svg = '<svg xmlns="http://www.w3.org/2000/svg" width="96" height="96" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">' + p + '</svg>';
      const im = new Image();
      im.decoding = 'async';
      im.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg);
      return im;
    });
    return ICON_IMGS;
  }
  const RAMP = ['#ffffff', '#dceaff', '#bad7ff', '#8fc0ff', '#5fa6ff', '#2f8cff', '#0f7fff'];
  const LALPHA = [0, .34, .5, .64, .77, .87, .94];
  const LAYOUTS = {
    heroWide: [
      { x: .80, y: .46, r: .30, a: 1, c: '#0f7fff' }, { x: .99, y: .92, r: .24, a: .95, c: '#2a63ff' },
      { x: .57, y: 1.04, r: .21, a: .7, c: '#5a6bff' }, { x: 1.02, y: .04, r: .19, a: .8, c: '#3b94ff' },
      { x: .24, y: 1.08, r: .13, a: .45, c: '#7fb5ff' }
    ],
    heroTall: [
      { x: .52, y: .97, r: .5, a: 1, c: '#0f7fff' }, { x: 1.04, y: .66, r: .3, a: .85, c: '#2a63ff' },
      { x: -.06, y: .8, r: .3, a: .62, c: '#5a6bff' }, { x: .98, y: .02, r: .2, a: .5, c: '#3b94ff' }
    ],
    endWide: [
      { x: .80, y: .52, r: .29, a: 1, c: '#0f7fff' }, { x: 1.02, y: .08, r: .22, a: .85, c: '#2a63ff' },
      { x: .6, y: 1.02, r: .23, a: .72, c: '#5a6bff' }
    ],
    endTall: [
      { x: .55, y: .98, r: .46, a: 1, c: '#0f7fff' }, { x: 1.04, y: .6, r: .28, a: .8, c: '#2a63ff' },
      { x: -.04, y: .84, r: .26, a: .58, c: '#5a6bff' }
    ]
  };
  function roundRect(ctx, x, y, w, h, r) {
    ctx.beginPath();
    if (ctx.roundRect) { ctx.roundRect(x, y, w, h, r); return; }
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y, x + w, y + h, r);
    ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r);
    ctx.arcTo(x, y, x + w, y, r);
    ctx.closePath();
  }

  function Field(host) {
    const kind = host.getAttribute('data-vpg-field') || 'hero';
    const useIcons = host.getAttribute('data-icons') === '1';
    const section = host.parentNode;
    const tide = doc.createElement('canvas');
    const base = doc.createElement('canvas');
    const tiles = doc.createElement('canvas');
    tide.className = 'vpg-field__tide';
    host.appendChild(tide);
    host.appendChild(base);
    host.appendChild(tiles);
    host.classList.add('is-live');
    const tctx = tide.getContext('2d');
    const fctx = tiles.getContext('2d');
    const bctx = base.getContext('2d');
    const canBlur = typeof fctx.filter === 'string';

    let W = 0, H = 0, S = 1, dpr = 1, G = 52, cols = 0, rows = 0, TW = 0, TH = 0, TOY = 0, crop = 0;
    let layout = [], vals = null, levels = null, sprites = [], hot = null, built = '';
    let visible = false, raf = 0, born = 0, introT = REDUCE ? 1 : 0, last = 0, clock = 0, baseAt = -1e9;
    const pointer = { x: 0, y: 0, inside: false, touched: false, t: 0 };
    const field = { x: 0, y: 0, open: 0 };
    const ripples = [];
    const icons = useIcons ? iconImages() : null;
    let iconLv = null, iconKey = '', iconTiles = [], clar = null, amb = null;
    const self = this;

    function resize() {
      const r = host.getBoundingClientRect();
      W = Math.max(1, Math.round(r.width));
      H = Math.max(1, Math.round(r.height));
      S = Math.max(W, H);
      dpr = Math.min(window.devicePixelRatio || 1, W < 600 ? 1.5 : 2);
      G = W < 600 ? 40 : W < 990 ? 46 : 52;
      const wide0 = W >= 990 && W / H > 1.05;
      /* on tall screens the drifting light and the icons stay in the lower part, so the
         per-frame canvas only covers that part (the whole field while a ripple runs) */
      crop = wide0 ? 0 : Math.round(H * .4);
      base.width = Math.round(W * dpr);
      base.height = Math.round(H * dpr);
      TOY = -1;
      setTop(crop);
      TW = Math.max(60, Math.round(W / 8));
      TH = Math.max(60, Math.round(H / 8));
      tide.width = TW; tide.height = TH;
      cols = Math.ceil(W / G) + 1;
      rows = Math.ceil(H / G) + 1;
      vals = new Float32Array(cols * rows);
      levels = new Uint8Array(cols * rows);
      clar = new Float32Array(cols * rows);
      amb = new Float64Array(cols * rows);
      const wide = W >= 990 && W / H > 1.05;
      layout = LAYOUTS[(kind === 'end' ? 'end' : 'hero') + (wide ? 'Wide' : 'Tall')];
      buildSprites();
      baseAt = -1e9;
      if (!pointer.touched) { field.x = W * (wide ? .78 : .5); field.y = H * (wide ? .46 : .86); }
      if (!visible || REDUCE) draw(performance.now());
    }

    function setTop(v) {
      if (v === TOY) return;
      TOY = v;
      tiles.width = Math.round(W * dpr);
      tiles.height = Math.max(1, Math.round((H - v) * dpr));
      tiles.style.top = v + 'px';
      tiles.style.height = (H - v) + 'px';
    }
    function buildSprites() {
      const key = G + ':' + dpr;
      if (key === built) return;
      built = key;
      const s = Math.max(4, Math.round(G * dpr));
      const gap = Math.max(1, Math.round(2.2 * dpr));
      const rad = 5 * dpr;
      sprites = RAMP.map((col) => {
        const c = doc.createElement('canvas');
        c.width = c.height = s;
        const x = c.getContext('2d');
        x.fillStyle = col;
        roundRect(x, gap, gap, s - gap * 2, s - gap * 2, rad); x.fill();
        const sheen = x.createLinearGradient(0, 0, s, s);
        sheen.addColorStop(0, 'rgba(255,255,255,0.30)');
        sheen.addColorStop(.45, 'rgba(255,255,255,0.05)');
        sheen.addColorStop(1, 'rgba(255,255,255,0)');
        x.fillStyle = sheen;
        roundRect(x, gap, gap, s - gap * 2, s - gap * 2, rad); x.fill();
        const shade = x.createLinearGradient(s, s, 0, 0);
        shade.addColorStop(0, 'rgba(8,30,80,0.12)');
        shade.addColorStop(.5, 'rgba(8,30,80,0)');
        x.fillStyle = shade;
        roundRect(x, gap, gap, s - gap * 2, s - gap * 2, rad); x.fill();
        x.strokeStyle = 'rgba(255,255,255,0.35)';
        x.lineWidth = Math.max(1, dpr * .8);
        roundRect(x, gap + .5 * dpr, gap + .5 * dpr, s - gap * 2 - dpr, s - gap * 2 - dpr, rad); x.stroke();
        return c;
      });
      const HS = 128;
      hot = doc.createElement('canvas');
      hot.width = hot.height = HS;
      const h = hot.getContext('2d');
      const hp = h.createRadialGradient(HS / 2, HS / 2, HS * .18, HS / 2, HS / 2, HS * .56);
      hp.addColorStop(0, 'rgba(255,255,255,0)');
      hp.addColorStop(.6, 'rgba(255,255,255,0.12)');
      hp.addColorStop(1, 'rgba(255,255,255,0.5)');
      h.fillStyle = hp; roundRect(h, 0, 0, HS, HS, HS * .1); h.fill();
      const hs = h.createLinearGradient(0, 0, HS, HS);
      hs.addColorStop(0, 'rgba(255,255,255,0.38)');
      hs.addColorStop(.5, 'rgba(255,255,255,0)');
      hs.addColorStop(1, 'rgba(8,30,80,0.2)');
      h.fillStyle = hs; roundRect(h, 0, 0, HS, HS, HS * .1); h.fill();
      iconKey = '';
    }
    function blobPos(b, i, t) {
      return {
        x: (b.x + Math.sin(t * (.07 + i * .013) + i * 1.7) * .035) * W,
        y: (b.y + Math.cos(t * (.055 + i * .011) + i * 2.3) * .04) * H,
        r: b.r * S * (1 + Math.sin(t * .09 + i) * .06)
      };
    }
    function landed(gx, gy) {
      if (introT >= 1) return 1;
      const d = ((cols - 1 - gx) / Math.max(1, cols)) * .6 + ((rows - 1 - gy) / Math.max(1, rows)) * .4;
      return smooth(introT * 1.9 - d * .85);
    }
    function hexA(hex, a) {
      const n = parseInt(hex.slice(1), 16);
      return 'rgba(' + (n >> 16 & 255) + ',' + (n >> 8 & 255) + ',' + (n & 255) + ',' + a.toFixed(3) + ')';
    }
    function rebuild(t) {
      tctx.fillStyle = '#ffffff';
      tctx.fillRect(0, 0, TW, TH);
      const kx = TW / W, ky = TH / H;
      const P = layout.map((b, i) => blobPos(b, i, t));
      layout.forEach((b, i) => {
        const p = P[i];
        const g = tctx.createRadialGradient(p.x * kx, p.y * ky, 0, p.x * kx, p.y * ky, p.r * 1.45 * kx);
        g.addColorStop(0, hexA(b.c, .62 * b.a));
        g.addColorStop(.55, hexA(b.c, .22 * b.a));
        g.addColorStop(1, hexA(b.c, 0));
        tctx.fillStyle = g;
        tctx.fillRect(0, 0, TW, TH);
      });
      for (let gx = 0; gx < cols; gx++) {
        for (let gy = 0; gy < rows; gy++) {
          const cx = gx * G + G / 2, cy = gy * G + G / 2;
          let v = 0;
          for (let i = 0; i < P.length; i++) {
            const dx = (cx - P[i].x) / P[i].r, dy = (cy - P[i].y) / P[i].r;
            v += layout[i].a * Math.exp(-(dx * dx + dy * dy) * 1.9);
          }
          v = clamp(v, 0, 1);
          const k = gy * cols + gx;
          vals[k] = v;
          levels[k] = Math.min(6, Math.floor(v * 7.4));
        }
      }
      bctx.setTransform(1, 0, 0, 1, 0, 0);
      bctx.clearRect(0, 0, base.width, base.height);
      const s = G * dpr;
      for (let gx = 0; gx < cols; gx++) {
        for (let gy = 0; gy < rows; gy++) {
          const L = levels[gy * cols + gx];
          if (L < 1) continue;
          const a = LALPHA[L] * landed(gx, gy);
          if (a < .01) continue;
          bctx.globalAlpha = a;
          bctx.drawImage(sprites[L], gx * s, gy * s, s, s);
        }
      }
      bctx.globalAlpha = 1;
      pickIconTiles();
    }
    function buildIcons() {
      if (!icons) return;
      const key = G + ':' + dpr;
      if (key !== iconKey) { iconKey = key; iconLv = icons.map(() => null); }
      const box = Math.round(G * .44 * dpr), pad = Math.ceil(6 * dpr * 2), side = box + pad * 2, LV = 5;
      let budget = 2;
      iconLv = iconLv.map((lv, n) => {
        if (lv || budget <= 0) return lv;
        const im = icons[n];
        if (!(im.complete && im.naturalWidth)) return null;
        budget--;
        const out = [];
        for (let k = 0; k < LV; k++) {
          const r = 6 * dpr * k / (LV - 1);
          const c = doc.createElement('canvas');
          c.width = c.height = side;
          const x = c.getContext('2d');
          if (r < .1) x.drawImage(im, pad, pad, box, box);
          else if (canBlur) { x.filter = 'blur(' + r.toFixed(2) + 'px)'; x.drawImage(im, pad, pad, box, box); x.filter = 'none'; }
          else {
            const q = Math.max(3, Math.round(box / (1 + r * .9)));
            const tmp = doc.createElement('canvas');
            tmp.width = tmp.height = q;
            tmp.getContext('2d').drawImage(im, 0, 0, q, q);
            x.drawImage(tmp, 0, 0, q, q, pad - r * .5, pad - r * .5, box + r, box + r);
          }
          out.push(c);
        }
        out.side = side / dpr;
        return out;
      });
    }
    function pickIconTiles() {
      if (!icons) return;
      iconTiles = [];
      for (let gx = 0; gx < cols; gx++) {
        for (let gy = 0; gy < rows; gy++) {
          const k = gy * cols + gx;
          if (levels[k] < 4) continue;
          if (hash(gx * 1.7 + 3.1, gy * 2.3 + 7.7) > .38) continue;
          iconTiles.push({ k, gx, gy, w: vals[k] * landed(gx, gy), ic: Math.floor(hash(gx + 17.3, gy - 9.1) * icons.length) % icons.length });
        }
      }
    }
    function waveAt(x, y) {
      let v = 0;
      const band = G * 1.5;
      for (let i = 0; i < ripples.length; i++) {
        const rp = ripples[i];
        const d = Math.sqrt((x - rp.x) * (x - rp.x) + (y - rp.y) * (y - rp.y));
        const b = 1 - Math.abs(d - rp.r) / band;
        if (b > 0) v = Math.max(v, b * (1 - rp.r / rp.max));
      }
      return v;
    }
    function draw(now) {
      const dt = Math.min(64, now - (last || now));
      last = now;
      if (!REDUCE) clock += dt / 1000;
      if (introT < 1 && born) introT = clamp((now - born - 200) / 1500, 0, 1);
      /* the colour wash drifts a few px per second, so the cube layer is redrawn a few times a second */
      if (introT < 1 || now - baseAt > (W < 600 ? 320 : 240)) { baseAt = now; rebuild(clock); }

      let tx, ty;
      const wide = W >= 990 && W / H > 1.05;
      if (pointer.inside) { tx = pointer.x; ty = pointer.y; }
      else {
        const t = now / 1000;
        tx = W * (wide ? .74 + Math.sin(t * .43) * .17 : .5 + Math.sin(t * .43) * .32);
        ty = H * (wide ? .5 + Math.sin(t * .71 + 1.1) * .2 : .82 + Math.sin(t * .71 + 1.1) * .1);
        if (REDUCE) { tx = W * .72; ty = H * .5; }
      }
      const f = 1 - Math.pow(1 - .12, dt / 16.667);
      field.x += (tx - field.x) * f;
      field.y += (ty - field.y) * f;
      field.open += (1 - field.open) * Math.min(1, dt / 260);
      for (let q = ripples.length - 1; q >= 0; q--) {
        ripples[q].r = (now - ripples[q].t0) * ripples[q].speed;
        if (ripples[q].r > ripples[q].max) ripples.splice(q, 1);
      }

      setTop(ripples.length || pointer.inside ? 0 : crop);
      fctx.setTransform(1, 0, 0, 1, 0, 0);
      fctx.clearRect(0, 0, tiles.width, tiles.height);
      fctx.setTransform(dpr, 0, 0, dpr, 0, -TOY * dpr);

      const R = (W < 600 ? 190 : 280) * field.open;
      const gx0 = Math.max(0, Math.floor((field.x - R) / G)), gx1 = Math.min(cols - 1, Math.floor((field.x + R) / G));
      const gy0 = Math.max(0, Math.floor((field.y - R) / G)), gy1 = Math.min(rows - 1, Math.floor((field.y + R) / G));
      for (let gx = gx0; gx <= gx1; gx++) {
        for (let gy = gy0; gy <= gy1; gy++) {
          const cx = gx * G + G / 2, cy = gy * G + G / 2;
          const n = Math.sqrt((cx - field.x) * (cx - field.x) + (cy - field.y) * (cy - field.y)) / R;
          if (n >= 1) continue;
          let l = 1 - n;
          l += .07 * Math.sin(n * Math.PI * 2.6 - clock * 2.2) * l;
          l += .12 * (hash(gx, gy) - .5) * 2 * l;
          l = smooth(l);
          const k = gy * cols + gx;
          const a = l * Math.max(vals[k], .3) * landed(gx, gy);
          if (a < .02) continue;
          const L = Math.min(6, Math.max(2, levels[k] + 1));
          const half = (G / 2) * (1 + .16 * l);
          fctx.globalAlpha = a;
          fctx.drawImage(sprites[L], cx - half, cy - half, half * 2, half * 2);
          if (l > .12) { fctx.globalAlpha = a * .9; fctx.drawImage(hot, cx - half, cy - half, half * 2, half * 2); }
        }
      }
      if (ripples.length) {
        for (let gx = 0; gx < cols; gx++) {
          for (let gy = 0; gy < rows; gy++) {
            const k = gy * cols + gx;
            if (vals[k] < .12) continue;
            const wx = gx * G + G / 2, wy = gy * G + G / 2, wv = waveAt(wx, wy);
            if (wv < .03) continue;
            const hh = (G / 2) * (1 + .12 * wv);
            fctx.globalAlpha = wv * vals[k] * .9;
            fctx.drawImage(sprites[Math.min(6, levels[k] + 1)], wx - hh, wy - hh, hh * 2, hh * 2);
            fctx.drawImage(hot, wx - hh, wy - hh, hh * 2, hh * 2);
          }
        }
      }
      if (icons) {
        buildIcons();
        if (!REDUCE && Math.random() < dt / 700 && iconTiles.length) {
          const pick = iconTiles[(Math.random() * iconTiles.length) | 0];
          amb[pick.k] = now + 1500 + Math.random() * 1500;
        }
        const thaw = 1 - Math.pow(1 - .16, dt / 16.667), freeze = 1 - Math.pow(1 - .05, dt / 16.667);
        for (let m = 0; m < iconTiles.length; m++) {
          const p = iconTiles[m], lv = iconLv && iconLv[p.ic];
          if (!lv) continue;
          const icx = p.gx * G + G / 2, icy = p.gy * G + G / 2;
          const near = R > 1 ? smooth(1 - Math.sqrt((icx - field.x) * (icx - field.x) + (icy - field.y) * (icy - field.y)) / R) : 0;
          const target = REDUCE ? (hash(p.gx + 5.5, p.gy + 1.3) < .35 ? .7 : 0)
            : Math.max(smooth((near - .2) / .55), now < amb[p.k] ? 1 : 0, ripples.length ? Math.min(1, waveAt(icx, icy) * 1.6) : 0);
          let c = clar[p.k];
          c += (target - c) * (target > c ? thaw : freeze);
          clar[p.k] = c;
          const alpha = p.w * (.26 + .74 * c);
          if (alpha < .02) continue;
          const sc = (.86 + .14 * c) * (1 + .16 * near);
          const sd = lv.side * sc;
          const fl = (1 - c) * (lv.length - 1), k0 = Math.floor(fl), k1 = Math.min(lv.length - 1, k0 + 1), tt = fl - k0;
          fctx.globalAlpha = alpha * (1 - tt);
          fctx.drawImage(lv[k0], icx - sd / 2, icy - sd / 2, sd, sd);
          if (tt > .01) { fctx.globalAlpha = alpha * tt; fctx.drawImage(lv[k1], icx - sd / 2, icy - sd / 2, sd, sd); }
        }
      }
      fctx.globalAlpha = 1;
      fctx.setTransform(1, 0, 0, 1, 0, 0);
    }
    let lastDraw = 0;
    function frame(now) {
      raf = 0;
      if (!visible) return;
      /* full rate only while the pointer is moving, a ripple runs or the field is landing;
         30 fps otherwise, 24 on phones */
      const busy = W >= 600 && (now - pointer.t < 600 || ripples.length || introT < 1);
      if (busy || now - lastDraw > (W < 600 ? 38 : 30)) { lastDraw = now; draw(now); }
      raf = requestAnimationFrame(frame);
    }
    function start() {
      if (visible) return;
      visible = true;
      if (!born) born = performance.now();
      last = 0;
      if (REDUCE) { draw(performance.now()); return; }
      if (!raf) raf = requestAnimationFrame(frame);
    }
    function stop() { visible = false; if (raf) cancelAnimationFrame(raf); raf = 0; }

    this.ripple = (clientX, clientY, big) => {
      if (REDUCE) return;
      const r = host.getBoundingClientRect();
      ripples.push({ x: clientX - r.left, y: clientY - r.top, t0: performance.now(), r: 0, speed: big ? 1.1 : .85, max: big ? Math.sqrt(W * W + H * H) : G * 8 });
      if (ripples.length > 6) ripples.shift();
    };
    const move = (e) => {
      const r = host.getBoundingClientRect();
      const x = e.clientX - r.left, y = e.clientY - r.top;
      pointer.inside = x >= 0 && y >= 0 && x <= r.width && y <= r.height && e.pointerType !== 'touch';
      if (pointer.inside) { pointer.x = x; pointer.y = y; pointer.touched = true; pointer.t = performance.now(); }
    };
    const tap = (e) => {
      const t = e.target;
      if (t && t.closest && t.closest('a,button,input,label,select,textarea,form,.vpg-dev')) return;
      self.ripple(e.clientX, e.clientY, false);
    };
    window.addEventListener('pointermove', move, { passive: true });
    section.addEventListener('pointerdown', tap, { passive: true });
    const ro = 'ResizeObserver' in window ? new ResizeObserver(() => resize()) : null;
    if (ro) ro.observe(host); else window.addEventListener('resize', resize);
    resize();
    this.io = onScreen(host, start, stop, 0);
    doc.addEventListener('visibilitychange', () => { if (doc.hidden) stop(); else if (inView(host)) start(); });
    this.destroy = () => { stop(); if (this.io) this.io.disconnect(); if (ro) ro.disconnect(); window.removeEventListener('pointermove', move); };
  }

  /* ===================================== glass-cube field, WebGL2 version
     A floor of frosted glass cubes seen at an angle, like the brand film. Blue
     light drifts across it, cubes rise and light up under the pointer, key
     presses send ripples, and whatever floats above (the device) casts a soft
     shadow. A tilt-shift blur gives the film's depth of field. Falls back to
     the 2D field when WebGL2 is missing or the context is lost. */
  function glPersp(fovy, asp, n, f) {
    const t = 1 / Math.tan(fovy / 2), nf = 1 / (n - f);
    return new Float32Array([t / asp, 0, 0, 0, 0, t, 0, 0, 0, 0, (f + n) * nf, -1, 0, 0, 2 * f * n * nf, 0]);
  }
  function glLookAt(e, c) {
    let zx = e[0] - c[0], zy = e[1] - c[1], zz = e[2] - c[2];
    let l = Math.hypot(zx, zy, zz); zx /= l; zy /= l; zz /= l;
    let xx = zz, xy = 0, xz = -zx;
    l = Math.hypot(xx, xy, xz); xx /= l; xy /= l; xz /= l;
    const yx = zy * xz - zz * xy, yy = zz * xx - zx * xz, yz = zx * xy - zy * xx;
    return new Float32Array([xx, yx, zx, 0, xy, yy, zy, 0, xz, yz, zz, 0,
      -(xx * e[0] + xy * e[1] + xz * e[2]), -(yx * e[0] + yy * e[1] + yz * e[2]), -(zx * e[0] + zy * e[1] + zz * e[2]), 1]);
  }
  /* a rounded box whose grid lines crowd the edges, so the bevels stay round
     with few triangles (no bottom face: it is never seen) */
  function glRoundedBox(hx, hy, hz, r) {
    const pos = [], nor = [], idx = [];
    const faces = [
      [[1, 0, 0], [0, 0, -1], [0, 1, 0]], [[-1, 0, 0], [0, 0, 1], [0, 1, 0]],
      [[0, 1, 0], [1, 0, 0], [0, 0, -1]],
      [[0, 0, 1], [1, 0, 0], [0, 1, 0]], [[0, 0, -1], [-1, 0, 0], [0, 1, 0]]
    ];
    const H = [hx, hy, hz], I = [hx - r, hy - r, hz - r];
    const steps = (h, inner) => { const e = inner / h, m = 1 - e; return [-1, -1 + m * .3, -1 + m * .68, -e, e, 1 - m * .68, 1 - m * .3, 1]; };
    faces.forEach((f) => {
      const N = f[0], U = f[1], V = f[2];
      const base = pos.length / 3;
      const ua = U.findIndex((x) => x !== 0), va = V.findIndex((x) => x !== 0);
      const su = steps(H[ua], I[ua]), sv = steps(H[va], I[va]);
      const n = su.length - 1;
      for (let j = 0; j <= n; j++) {
        for (let i = 0; i <= n; i++) {
          const v = [0, 1, 2].map((a) => (N[a] + U[a] * su[i] + V[a] * sv[j]) * H[a]);
          const c = v.map((x, a) => clamp(x, -I[a], I[a]));
          let d = [v[0] - c[0], v[1] - c[1], v[2] - c[2]];
          let l = Math.hypot(d[0], d[1], d[2]);
          if (l < 1e-6) { d = N.slice(); l = 1; }
          pos.push(c[0] + d[0] / l * r, c[1] + d[1] / l * r, c[2] + d[2] / l * r);
          nor.push(d[0] / l, d[1] / l, d[2] / l);
        }
      }
      for (let j = 0; j < n; j++) {
        for (let i = 0; i < n; i++) {
          const a = base + j * (n + 1) + i, b = a + 1, c2 = a + n + 1, d2 = c2 + 1;
          idx.push(a, c2, b, b, c2, d2);
        }
      }
    });
    return { pos: new Float32Array(pos), nor: new Float32Array(nor), idx: new Uint16Array(idx) };
  }
  const GL_BLUE = 'float blueAt(vec2 p){ float b=0.; for(int i=0;i<5;i++){ vec2 d=(p-uBlob[i].xy)/uBlob[i].z; b+=uBlob[i].w*exp(-dot(d,d)); } return b; }';
  const GL_CUBE_VS = `#version 300 es
precision highp float;
layout(location=0) in vec3 aPos;
layout(location=1) in vec3 aNor;
layout(location=2) in vec3 aInst;
uniform mat4 uProj, uView;
uniform float uTime, uIntro;
uniform vec3 uPtr;
uniform vec4 uRip[6];
uniform vec4 uBlob[5];
out vec3 vN; out vec3 vW; out float vBlue; out float vH;
${GL_BLUE}
void main(){
  vec2 g = aInst.xy; float seed = aInst.z;
  float bump = uPtr.z * smoothstep(3.4, 0.0, length(g - uPtr.xy));
  float rip = 0.;
  for(int i=0;i<6;i++){
    float age = uTime - uRip[i].z;
    if(age > 0. && age < 3.2){
      float d = abs(length(g - uRip[i].xy) - age * 7.0);
      rip += uRip[i].w * smoothstep(1.6, 0., d) * (1. - age / 3.2);
    }
  }
  float idle = 0.035 * sin(uTime * 0.9 + seed * 6.2831);
  float intro = smoothstep(0., 1., uIntro * 1.7 - seed * 0.7);
  float lift = bump * 0.75 + rip * 0.55 + idle;
  vec3 p = aPos + vec3(g.x, lift - (1. - intro) * 2.2, g.y);
  vW = p; vN = aNor; vH = aPos.y;
  vBlue = clamp(blueAt(g) + bump * 0.55 + rip * 0.7, 0., 1.);
  gl_Position = uProj * uView * vec4(p, 1.);
}`;
  const GL_CUBE_FS = `#version 300 es
precision highp float;
in vec3 vN; in vec3 vW; in float vBlue; in float vH;
uniform vec3 uCam, uBg, uFrost, uBlue; uniform float uTime; uniform vec4 uDev;
out vec4 o;
void main(){
  vec3 n = normalize(vN);
  vec3 V = normalize(uCam - vW);
  vec3 L = normalize(vec3(-0.5, 1.0, 0.45));
  float top = smoothstep(0.6, 0.98, n.y);
  float b = smoothstep(0.1, 0.72, vBlue);
  vec3 base = mix(mix(vec3(0.86, 0.89, 0.95), uFrost, top), mix(uBlue * vec3(0.78, 0.84, 0.92), mix(uBlue, vec3(1.0), 0.1), top), b);
  vec3 col = base * (0.72 + 0.28 * max(dot(n, L), 0.));
  col += (1.0 - smoothstep(-0.2, -0.04, vH)) * mix(vec3(0.2), vec3(0.06, 0.16, 0.4), b);
  col += pow(1.0 - max(dot(n, V), 0.), 3.0) * mix(vec3(0.32), vec3(0.18, 0.32, 0.62), b);
  col += pow(max(dot(n, normalize(L + V)), 0.), 60.) * 0.55;
  float u = vW.x * 0.78 + vW.z * 0.62 + sin(uTime * 0.05) * 2.0;
  col *= 1.0 - 0.18 * smoothstep(0.35, 1.0, sin(u * 0.33));
  if (uDev.z > 0.) { vec2 d = (vW.xz - uDev.xy) / uDev.z; col *= 1.0 - 0.3 * exp(-dot(d, d) * 1.4) * uDev.w; }
  o = vec4(mix(col, uBg, smoothstep(16., 30., length(uCam - vW))), 1.);
}`;
  const GL_FLOOR_VS = `#version 300 es
precision highp float;
layout(location=0) in vec2 aXZ;
uniform mat4 uProj, uView;
out vec2 vXZ; out vec3 vW;
void main(){ vXZ = aXZ; vW = vec3(aXZ.x, -0.23, aXZ.y); gl_Position = uProj * uView * vec4(vW, 1.); }`;
  const GL_FLOOR_FS = `#version 300 es
precision highp float;
in vec2 vXZ; in vec3 vW;
uniform vec4 uBlob[5]; uniform vec3 uCam, uBg;
out vec4 o;
${GL_BLUE}
void main(){
  vec3 col = mix(vec3(1.0), vec3(0.78, 0.9, 1.0), smoothstep(0.1, 0.9, blueAt(vXZ)));
  o = vec4(mix(col, uBg, smoothstep(16., 30., length(uCam - vW))), 1.);
}`;
  const GL_QUAD_VS = `#version 300 es
layout(location=0) in vec2 aP;
out vec2 vUv;
void main(){ vUv = aP * 0.5 + 0.5; gl_Position = vec4(aP, 0., 1.); }`;
  const GL_BLUR_FS = `#version 300 es
precision highp float;
in vec2 vUv; uniform sampler2D uTex; uniform vec2 uDir; out vec4 o;
void main(){
  vec4 c = texture(uTex, vUv) * 0.2270270270;
  c += texture(uTex, vUv + uDir * 1.3846153846) * 0.3162162162;
  c += texture(uTex, vUv - uDir * 1.3846153846) * 0.3162162162;
  c += texture(uTex, vUv + uDir * 3.2307692308) * 0.0702702703;
  c += texture(uTex, vUv - uDir * 3.2307692308) * 0.0702702703;
  o = c;
}`;
  const GL_COMP_FS = `#version 300 es
precision highp float;
in vec2 vUv; uniform sampler2D uSharp, uSoft; uniform vec2 uFocus; uniform vec4 uClear; uniform vec3 uBg; uniform float uFadeB; out vec4 o;
void main(){
  float m = smoothstep(uFocus.y * 0.35, uFocus.y, abs(vUv.y - uFocus.x));
  m = max(m, smoothstep(0.82, 1.0, vUv.y) * 0.9);
  vec3 c = mix(texture(uSharp, vUv).rgb, texture(uSoft, vUv).rgb, m);
  /* a quiet, soft area behind the words */
  float a = uClear.x < 0.5 ? 1.0 - smoothstep(uClear.y, uClear.z, vUv.x) : smoothstep(uClear.y, uClear.z, vUv.y);
  c = mix(c, mix(c, texture(uSoft, vUv).rgb, 0.85), a);
  c = mix(c, uBg, a * uClear.w);
  /* melt into the next section along the bottom edge */
  o = vec4(mix(c, uBg, (1.0 - smoothstep(0.0, 0.12, vUv.y)) * uFadeB), 1.);
}`;
  function glProgram(gl, vs, fs) {
    const p = gl.createProgram();
    [[gl.VERTEX_SHADER, vs], [gl.FRAGMENT_SHADER, fs]].forEach((d) => {
      const s = gl.createShader(d[0]); gl.shaderSource(s, d[1]); gl.compileShader(s);
      if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(s) || 'shader');
      gl.attachShader(p, s);
    });
    gl.linkProgram(p);
    if (!gl.getProgramParameter(p, gl.LINK_STATUS)) throw new Error(gl.getProgramInfoLog(p) || 'link');
    const u = {};
    const n = gl.getProgramParameter(p, gl.ACTIVE_UNIFORMS);
    for (let i = 0; i < n; i++) { const info = gl.getActiveUniform(p, i); u[info.name.replace(/\[0\]$/, '')] = gl.getUniformLocation(p, info.name); }
    return { p, u };
  }
  /* ?vpg-gl=1 keeps the WebGL field on software renderers (for checking it) */
  const FORCE_GL = /[?&]vpg-gl=1/.test(location.search);
  const GL_LAYOUTS = {
    heroWide: [[3.5, -0.5, 5.2, 1], [8.5, 5.5, 4.6, .9], [9.5, -7, 4.2, .8], [-1, 6.5, 4, .55], [-7, -5, 3, .25]],
    heroTall: [[0.5, 7, 6, 1], [5.5, 2, 4.5, .85], [-5, 5, 4.4, .6], [2, 12, 5, .75], [-5, -7, 3, .3]],
    endWide: [[4, 1, 5.5, 1], [9, -6, 4.5, .85], [-2, 7, 5, .7], [12, 5, 4, .6], [-8, -4, 3, .25]],
    endTall: [[1, 8, 6, 1], [6, 2, 4, .8], [-5, 6, 4.5, .6], [3, 13, 5, .7], [-6, -6, 3, .2]]
  };

  function GLField(host) {
    const kind = host.getAttribute('data-vpg-field') || 'hero';
    const section = host.parentNode;
    const canvas = doc.createElement('canvas');
    canvas.className = 'vpg-field__gl';
    let gl = null;
    try { gl = canvas.getContext('webgl2', { antialias: false, alpha: false, depth: true, stencil: false, powerPreference: 'high-performance' }); } catch (e) { gl = null; }
    if (!gl) return null;
    const drop = () => { try { const lc = gl.getExtension('WEBGL_lose_context'); if (lc) lc.loseContext(); } catch (e) { /* ignore */ } return null; };
    /* a software renderer would crawl: use the 2D field instead */
    try {
      const dbg = gl.getExtension('WEBGL_debug_renderer_info');
      const rn = dbg ? String(gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL)) : '';
      if (/swiftshader|llvmpipe|softpipe|software|basic render/i.test(rn) && !FORCE_GL) return drop();
    } catch (e) { /* keep going */ }
    let P;
    try {
      P = {
        cube: glProgram(gl, GL_CUBE_VS, GL_CUBE_FS), floor: glProgram(gl, GL_FLOOR_VS, GL_FLOOR_FS),
        blur: glProgram(gl, GL_QUAD_VS, GL_BLUR_FS), comp: glProgram(gl, GL_QUAD_VS, GL_COMP_FS)
      };
    } catch (e) { swallow(e); return drop(); }
    host.appendChild(canvas);
    host.classList.add('is-live', 'is-gl');

    const box = glRoundedBox(0.455, 0.22, 0.455, 0.09);
    const buf = (target, data) => { const b = gl.createBuffer(); gl.bindBuffer(target, b); gl.bufferData(target, data, gl.STATIC_DRAW); return b; };
    const vaoCube = gl.createVertexArray();
    gl.bindVertexArray(vaoCube);
    buf(gl.ARRAY_BUFFER, box.pos); gl.enableVertexAttribArray(0); gl.vertexAttribPointer(0, 3, gl.FLOAT, false, 0, 0);
    buf(gl.ARRAY_BUFFER, box.nor); gl.enableVertexAttribArray(1); gl.vertexAttribPointer(1, 3, gl.FLOAT, false, 0, 0);
    buf(gl.ELEMENT_ARRAY_BUFFER, box.idx);
    const bInst = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, bInst);
    gl.enableVertexAttribArray(2); gl.vertexAttribPointer(2, 3, gl.FLOAT, false, 0, 0); gl.vertexAttribDivisor(2, 1);
    gl.bindVertexArray(null);
    const vaoFloor = gl.createVertexArray();
    gl.bindVertexArray(vaoFloor);
    buf(gl.ARRAY_BUFFER, new Float32Array([-60, -60, 60, -60, -60, 60, 60, 60])); gl.enableVertexAttribArray(0); gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
    gl.bindVertexArray(null);
    const vaoQuad = gl.createVertexArray();
    gl.bindVertexArray(vaoQuad);
    buf(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1])); gl.enableVertexAttribArray(0); gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
    gl.bindVertexArray(null);

    const samples = Math.min(4, gl.getParameter(gl.MAX_SAMPLES) || 0);
    const BG = new Float32Array([1, 1, 1]), FROST = new Float32Array([0.97, 0.975, 0.99]), BLUE = new Float32Array([0.06, 0.46, 1.0]);
    const blobs = new Float32Array(20), rip = new Float32Array(24), dev = new Float32Array(4);
    const ptr = [0, 0, 0], ptrT = [0, 0, 0];
    let W = 0, H = 0, dpr = 1, count = 0, ripN = 0, t = 0, intro = REDUCE ? 1 : 0, wide = true;
    let ms = null, sharp = null, soft = [], view = null, proj = null, eye = [0, 0, 0];
    let visible = false, born = false, lastDraw = 0, lastPtr = -1e9, lastRip = -1e9, lost = false, frameN = 0;
    const floater = section.querySelector('.vpg-hero__float .vpg-dev, .vpg-end__float');

    function target(w, h) {
      const t2 = gl.createTexture(); gl.bindTexture(gl.TEXTURE_2D, t2);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA8, w, h, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR); gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE); gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      const f = gl.createFramebuffer(); gl.bindFramebuffer(gl.FRAMEBUFFER, f);
      gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, t2, 0);
      return { t: t2, f, w, h };
    }
    function free(x) { if (!x) return; if (x.t) gl.deleteTexture(x.t); if (x.f) gl.deleteFramebuffer(x.f); if (x.rb) x.rb.forEach((r) => gl.deleteRenderbuffer(r)); }
    function onFloor(x, z) {
      for (const y of [-0.3, 1.6]) {
        const v = view, p = proj;
        const cx = v[0] * x + v[4] * y + v[8] * z + v[12], cy = v[1] * x + v[5] * y + v[9] * z + v[13], cz = v[2] * x + v[6] * y + v[10] * z + v[14];
        if (-cz <= .1) continue;
        if (Math.abs(p[0] * cx / -cz) < 1.25 && Math.abs(p[5] * cy / -cz) < 1.25) return true;
      }
      return false;
    }
    function resize() {
      if (lost) return;
      const r = host.getBoundingClientRect();
      W = Math.max(2, Math.round(r.width)); H = Math.max(2, Math.round(r.height));
      dpr = Math.min(window.devicePixelRatio || 1, W < 700 ? 1.5 : 1.75);
      const w = Math.round(W * dpr), h = Math.round(H * dpr);
      canvas.width = w; canvas.height = h;
      free(ms); free(sharp); soft.forEach(free);
      const f = gl.createFramebuffer(); gl.bindFramebuffer(gl.FRAMEBUFFER, f);
      const rb = [gl.createRenderbuffer(), gl.createRenderbuffer()];
      gl.bindRenderbuffer(gl.RENDERBUFFER, rb[0]);
      if (samples) gl.renderbufferStorageMultisample(gl.RENDERBUFFER, samples, gl.RGBA8, w, h); else gl.renderbufferStorage(gl.RENDERBUFFER, gl.RGBA8, w, h);
      gl.framebufferRenderbuffer(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.RENDERBUFFER, rb[0]);
      gl.bindRenderbuffer(gl.RENDERBUFFER, rb[1]);
      if (samples) gl.renderbufferStorageMultisample(gl.RENDERBUFFER, samples, gl.DEPTH_COMPONENT24, w, h); else gl.renderbufferStorage(gl.RENDERBUFFER, gl.DEPTH_COMPONENT24, w, h);
      gl.framebufferRenderbuffer(gl.FRAMEBUFFER, gl.DEPTH_ATTACHMENT, gl.RENDERBUFFER, rb[1]);
      ms = { f, rb, w, h };
      sharp = target(w, h);
      const qw = Math.max(2, w >> 2), qh = Math.max(2, h >> 2);
      soft = [target(qw, qh), target(qw, qh)];
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      /* the camera: the grid turned like the film, seen from above at an angle */
      wide = W / H > 1.05;
      const yaw = -0.62, dist = wide ? 11.5 : 14, pitch = wide ? 0.92 : 1.0;
      const tx = wide ? 2.6 : 0, tz = wide ? 0.4 : 3.2;
      eye = [tx + Math.sin(yaw) * Math.cos(pitch) * dist, Math.sin(pitch) * dist, tz + Math.cos(yaw) * Math.cos(pitch) * dist];
      view = glLookAt(eye, [tx, 0, tz]);
      proj = glPersp(wide ? 0.6 : 0.68, W / H, 1, 80);
      const data = [];
      for (let z = -22; z <= 22; z++) {
        for (let x = -22; x <= 22; x++) if (onFloor(x, z)) data.push(x, z, hash(x + 31.7, z - 12.9));
      }
      count = data.length / 3;
      gl.bindBuffer(gl.ARRAY_BUFFER, bInst);
      gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(data), gl.STATIC_DRAW);
      if (!visible || REDUCE) draw(performance.now(), 16);
    }
    /* where a point on screen lands on the floor */
    function toFloor(cx, cy) {
      const r = canvas.getBoundingClientRect();
      const dx = (((cx - r.left) / r.width) * 2 - 1) / proj[0], dy = (1 - ((cy - r.top) / r.height) * 2) / proj[5];
      const v = view;
      const dir = [v[0] * dx + v[1] * dy - v[2], v[4] * dx + v[5] * dy - v[6], v[8] * dx + v[9] * dy - v[10]];
      const k = dir[1] < -1e-4 ? -eye[1] / dir[1] : 40;
      return [eye[0] + dir[0] * k, eye[2] + dir[2] * k];
    }
    function setBlobs() {
      const L = GL_LAYOUTS[(kind === 'end' ? 'end' : 'hero') + (wide ? 'Wide' : 'Tall')];
      L.forEach((b, i) => {
        blobs[i * 4] = b[0] + Math.sin(t * (0.07 + i * 0.013) + i * 1.7) * 1.2;
        blobs[i * 4 + 1] = b[1] + Math.cos(t * (0.06 + i * 0.011) + i * 2.3) * 1.2;
        blobs[i * 4 + 2] = b[2] * (1 + Math.sin(t * 0.09 + i) * 0.06);
        blobs[i * 4 + 3] = b[3];
      });
    }
    function shadow() {
      if (!floater || W < 2) { dev[2] = 0; return; }
      const r = floater.getBoundingClientRect(), hr = host.getBoundingClientRect();
      if (!r.width || r.bottom < hr.top || r.top > hr.bottom) { dev[2] = 0; return; }
      const f = toFloor(r.left + r.width * .55, r.bottom - r.height * .12), g = toFloor(r.left + r.width * 1.15, r.bottom - r.height * .12);
      dev[0] = f[0]; dev[1] = f[1]; dev[2] = Math.max(1.5, Math.hypot(g[0] - f[0], g[1] - f[1])); dev[3] = 1;
    }
    function draw(now, dt) {
      if (lost || !ms) return;
      if (!REDUCE) t += dt / 1000;
      if (born && intro < 1) intro = Math.min(1, intro + dt / 1800);
      const k = 1 - Math.pow(1 - .12, dt / 16.667);
      for (let i = 0; i < 3; i++) ptr[i] += (ptrT[i] - ptr[i]) * k;
      setBlobs();
      if (frameN++ % 6 === 0) shadow();
      const w = ms.w, h = ms.h;
      gl.bindFramebuffer(gl.FRAMEBUFFER, ms.f);
      gl.viewport(0, 0, w, h);
      gl.clearColor(BG[0], BG[1], BG[2], 1);
      gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
      gl.enable(gl.DEPTH_TEST);
      gl.useProgram(P.floor.p);
      gl.uniformMatrix4fv(P.floor.u.uProj, false, proj); gl.uniformMatrix4fv(P.floor.u.uView, false, view);
      gl.uniform4fv(P.floor.u.uBlob, blobs); gl.uniform3fv(P.floor.u.uCam, eye); gl.uniform3fv(P.floor.u.uBg, BG);
      gl.disable(gl.CULL_FACE);
      gl.bindVertexArray(vaoFloor); gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
      gl.enable(gl.CULL_FACE);
      const u = P.cube.u;
      gl.useProgram(P.cube.p);
      gl.uniformMatrix4fv(u.uProj, false, proj); gl.uniformMatrix4fv(u.uView, false, view);
      gl.uniform1f(u.uTime, t); gl.uniform1f(u.uIntro, intro);
      gl.uniform3fv(u.uPtr, ptr); gl.uniform4fv(u.uRip, rip); gl.uniform4fv(u.uBlob, blobs);
      gl.uniform3fv(u.uCam, eye); gl.uniform3fv(u.uBg, BG); gl.uniform3fv(u.uFrost, FROST); gl.uniform3fv(u.uBlue, BLUE);
      gl.uniform4fv(u.uDev, dev);
      gl.bindVertexArray(vaoCube);
      gl.drawElementsInstanced(gl.TRIANGLES, box.idx.length, gl.UNSIGNED_SHORT, 0, count);
      gl.disable(gl.DEPTH_TEST); gl.disable(gl.CULL_FACE);
      /* resolve, blur a quarter-size copy, mix it back outside the focus band */
      gl.bindFramebuffer(gl.READ_FRAMEBUFFER, ms.f); gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, sharp.f);
      gl.blitFramebuffer(0, 0, w, h, 0, 0, w, h, gl.COLOR_BUFFER_BIT, gl.NEAREST);
      gl.bindFramebuffer(gl.READ_FRAMEBUFFER, sharp.f); gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, soft[0].f);
      gl.blitFramebuffer(0, 0, w, h, 0, 0, soft[0].w, soft[0].h, gl.COLOR_BUFFER_BIT, gl.LINEAR);
      gl.useProgram(P.blur.p);
      gl.bindVertexArray(vaoQuad);
      gl.viewport(0, 0, soft[0].w, soft[0].h);
      gl.activeTexture(gl.TEXTURE0); gl.uniform1i(P.blur.u.uTex, 0);
      for (let it = 0; it < 2; it++) {
        gl.bindFramebuffer(gl.FRAMEBUFFER, soft[1].f); gl.bindTexture(gl.TEXTURE_2D, soft[0].t);
        gl.uniform2f(P.blur.u.uDir, 1 / soft[0].w, 0); gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
        gl.bindFramebuffer(gl.FRAMEBUFFER, soft[0].f); gl.bindTexture(gl.TEXTURE_2D, soft[1].t);
        gl.uniform2f(P.blur.u.uDir, 0, 1 / soft[0].h); gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
      }
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, w, h);
      gl.useProgram(P.comp.p);
      gl.activeTexture(gl.TEXTURE0); gl.bindTexture(gl.TEXTURE_2D, sharp.t); gl.uniform1i(P.comp.u.uSharp, 0);
      gl.activeTexture(gl.TEXTURE1); gl.bindTexture(gl.TEXTURE_2D, soft[0].t); gl.uniform1i(P.comp.u.uSoft, 1);
      gl.uniform2f(P.comp.u.uFocus, wide ? .5 : .42, wide ? .42 : .5);
      /* where the words sit: the left on wide screens, the top on tall ones */
      if (wide) gl.uniform4f(P.comp.u.uClear, 0, kind === 'end' ? .3 : .26, kind === 'end' ? .62 : .58, .88);
      else gl.uniform4f(P.comp.u.uClear, 1, kind === 'end' ? .36 : .44, kind === 'end' ? .58 : .64, .9);
      gl.uniform3fv(P.comp.u.uBg, BG);
      gl.uniform1f(P.comp.u.uFadeB, kind === 'end' ? 0 : .9);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
      gl.activeTexture(gl.TEXTURE0);
      gl.bindVertexArray(null);
    }
    const gaps = [];
    let guarded = FORCE_GL;
    const self = this;
    function frame(now, dt) {
      if (!visible || lost) return;
      /* full rate while something moves under the pointer, a ripple runs or the cubes land; 30 fps otherwise */
      const busy = now - lastPtr < 700 || t - lastRip < 3.2 || intro < 1;
      if (!busy && now - lastDraw < 30) return;
      /* if this device can't keep up, hand over to the 2D field */
      if (!guarded && lastDraw && busy) {
        gaps.push(now - lastDraw);
        if (gaps.length >= 24) {
          guarded = true;
          const g = gaps.slice().sort((a, b) => a - b)[12];
          if (g > 55) { lost = true; stop(); canvas.remove(); host.classList.remove('is-gl'); if (self.onLost) self.onLost(); return; }
        }
      }
      draw(now, Math.min(64, now - (lastDraw || now - dt)));
      lastDraw = now;
    }
    function start() {
      if (visible || lost) return;
      visible = true; born = true; lastDraw = 0;
      if (REDUCE) { draw(performance.now(), 16); return; }
      Ticker.add(frame);
    }
    function stop() { visible = false; Ticker.remove(frame); }

    this.ripple = (clientX, clientY, big) => {
      if (REDUCE || lost || !proj) return;
      const f = toFloor(clientX, clientY), i = (ripN++ % 6) * 4;
      rip[i] = f[0]; rip[i + 1] = f[1]; rip[i + 2] = t; rip[i + 3] = big ? 1.25 : 0.8;
      lastRip = t;
    };
    const move = (e) => {
      if (e.pointerType !== 'mouse' || REDUCE || !proj) return;
      const r = host.getBoundingClientRect();
      const inside = e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom;
      if (inside) {
        const f = toFloor(e.clientX, e.clientY);
        ptrT[0] = f[0]; ptrT[1] = f[1]; ptrT[2] = 1;
        if (ptr[2] < .01) { ptr[0] = f[0]; ptr[1] = f[1]; }
        lastPtr = performance.now();
      } else if (ptrT[2]) { ptrT[2] = 0; lastPtr = performance.now(); }
    };
    const tap = (e) => {
      const tg = e.target;
      if (tg && tg.closest && tg.closest('a,button,input,label,select,textarea,form,.vpg-dev')) return;
      this.ripple(e.clientX, e.clientY, false);
    };
    window.addEventListener('pointermove', move, { passive: true });
    section.addEventListener('pointerdown', tap, { passive: true });
    canvas.addEventListener('webglcontextlost', (e) => { e.preventDefault(); lost = true; stop(); host.classList.remove('is-gl'); canvas.style.display = 'none'; if (this.onLost) this.onLost(); });
    const ro = 'ResizeObserver' in window ? new ResizeObserver(() => resize()) : null;
    if (ro) ro.observe(host); else window.addEventListener('resize', resize);
    resize();
    this.io = onScreen(host, start, stop, 0);
    doc.addEventListener('visibilitychange', () => { if (doc.hidden) stop(); else if (inView(host)) start(); });
    this.destroy = () => { stop(); if (this.io) this.io.disconnect(); if (ro) ro.disconnect(); window.removeEventListener('pointermove', move); };
  }

  /* ============================================================= hero */
  /* Five pre-defined keypad setups. All 5 keys swap at once to the next
     set, so the hero demos "configurable device" instead of random label
     cycling. Key order: top-left | top-right | middle-left | middle-right | wide */
  const SETS = [
    ['Meeting Notes', 'Calendar', 'Slides', 'AI Workspace', 'Dictate'],
    ['Claude', 'Mode', 'Approve', 'Reject', 'Speak the prompt'],
    ['Salesforce', 'Gmail', 'Zoom', 'Slack', 'Dictate'],
    ['Figma', 'ChatGPT', 'Notion', 'Slack', 'Dictate'],
    ['YouTube', 'Instagram', 'ChatGPT', 'Notion', 'Dictate']
  ];
  function Hero(sec, field) {
    const stage = $('[data-vpg-tilt]', sec);
    const dev = stage && $('.vpg-dev', stage);
    if (!dev) return null;
    const keys = $$('.vpg-key', dev);
    const sheen = $('.vpg-dev__sheen', dev);
    let visible = false, timer = 0, setIdx = 0, hover = -1, booted = false;
    function light(i, ms) {
      const k = keys[i];
      k.classList.add('is-down');
      setTimeout(() => { k.classList.remove('is-down'); k.classList.add('is-lit'); }, 110);
      setTimeout(() => { if (hover !== i) k.classList.remove('is-lit'); }, ms || 1150);
    }
    /* Reveal keys one by one in order 1→2→3→4→5, with each key both
       lighting up and getting its new label at the same moment. */
    function applySet(idx) {
      const set = SETS[idx];
      set.forEach((label, i) => {
        if (!keys[i]) return;
        setTimeout(() => {
          setLegend(keys[i], label, true);
          if (!REDUCE) light(i, 620);
        }, i * 220);
      });
    }
    function tick() {
      if (!visible) return;
      setIdx = (setIdx + 1) % SETS.length;
      applySet(setIdx);
      /* 5 keys × 220ms stagger = ~1.1s to swap in, then ~2.9s to read */
      timer = setTimeout(tick, 4100);
    }
    function boot() {
      if (booted) return;
      booted = true;
      /* Start with all keys empty — labels fade in one-by-one with set 0 */
      keys.forEach((k) => setLegend(k, '', false));
      if (REDUCE) {
        applySet(0);
        keys[4].classList.add('is-lit');
        return;
      }
      /* Dramatic pause, then the first set appears key-by-key */
      setTimeout(() => {
        applySet(0);
        const r = dev.getBoundingClientRect();
        if (field) field.ripple(r.left + r.width / 2, r.top + r.height * .6, true);
      }, 650);
      /* Then the set rotation starts, cycling through all 5 setups */
      timer = setTimeout(tick, 4500);
    }
    keys.forEach((k, i) => {
      k.addEventListener('pointerenter', (e) => { if (e.pointerType === 'mouse') { hover = i; k.classList.add('is-lit'); } });
      k.addEventListener('pointerleave', () => { if (hover === i) hover = -1; k.classList.remove('is-lit'); });
      k.addEventListener('click', () => {
        light(i, 900);
        /* Clicking any key advances to the next set */
        setIdx = (setIdx + 1) % SETS.length;
        applySet(setIdx);
        if (field) { const r = k.getBoundingClientRect(); field.ripple(r.left + r.width / 2, r.top + r.height / 2, true); }
      });
    });

    /* tilt and the highlight on the metal */
    let tx = 0, ty = 0, x = 0, y = 0, ticking = false;
    const auto = !FINE;
    function apply(now) {
      if (auto) { tx = Math.sin(now / 1700) * .32; ty = Math.cos(now / 2300) * .2; }
      const k = .085;
      x += (tx - x) * k; y += (ty - y) * k;
      dev.style.transform = 'perspective(1100px) rotateX(' + (-y * 11).toFixed(2) + 'deg) rotateY(' + (x * 15).toFixed(2) + 'deg) rotateZ(-8deg)';
      if (sheen) sheen.style.transform = 'translate3d(' + (-66.667 * clamp(.5 - x * 1.1 + y * .35, 0, 1)).toFixed(2) + '%,0,0)';
      if (!auto && Math.abs(tx - x) < .001 && Math.abs(ty - y) < .001) { Ticker.remove(apply); ticking = false; }
    }
    function wake() { if (!ticking && !REDUCE) { ticking = true; Ticker.add(apply); } }
    if (FINE && !REDUCE) {
      sec.addEventListener('pointermove', (e) => {
        if (e.pointerType !== 'mouse') return;
        const r = sec.getBoundingClientRect();
        tx = (e.clientX - r.left) / r.width - .5; ty = (e.clientY - r.top) / r.height - .5;
        wake();
      }, { passive: true });
      sec.addEventListener('pointerleave', () => { tx = 0; ty = 0; wake(); });
    }

    const io = onScreen(sec, () => {
      if (visible) return;
      visible = true;
      if (!booted) setTimeout(boot, 1150); else timer = setTimeout(tick, 900);
      if (auto) wake();
    }, () => {
      visible = false; clearTimeout(timer);
      if (auto) { Ticker.remove(apply); ticking = false; }
    }, .1);
    return { destroy() { visible = false; clearTimeout(timer); Ticker.remove(apply); if (io) io.disconnect(); } };
  }

  /* ====================================================== key chapter */
  const KEYBOX = [[7.15, 23.33, 39.78, 21.96], [52.47, 23.33, 39.73, 21.96], [7.15, 48.38, 39.78, 21.96], [52.42, 48.38, 39.78, 21.99], [8.26, 73.44, 82.88, 21.99]];
  function Chapter(sec) {
    const pin = $('.vpg-ch__pin', sec);
    const film = $('.vpg-ch__film', sec);
    const canvas = $('.vpg-ch__canvas', sec);
    const cctx = canvas.getContext('2d');
    const t1 = $('.vpg-ch__t1', sec), t2 = $('.vpg-ch__t2', sec);
    const stage = $('[data-stage]', sec);
    const devWrap = $('[data-device]', sec);
    const dev = $('.vpg-dev', devWrap);
    const keys = $$('.vpg-key', dev);
    const glow = $('.vpg-ch__glow', sec);
    const tilesBox = $('[data-tiles]', sec);
    const copy = $('.vpg-ch__copy', sec);
    const caps = $$('.vpg-ch__cap', sec);
    const dots = $$('.vpg-ch__dots li', sec);
    const setupCaps = caps.filter((c) => c.getAttribute('data-cap') !== 'end');
    const setups = setupCaps.map((c) => (c.getAttribute('data-keys') || '').split('|').map((s) => s.trim()));
    const N = setups.length;
    if (!N || !dev) return null;

    if (REDUCE) {
      sec.classList.add('is-static');
      keys.forEach((k, i) => setLegend(k, setups[0][i] || '', false));
      return null;
    }

    const F = .17, S0 = .215, END = .8, STAG = .013;
    const SEG = (END - S0) / N;
    const ORDER = [4, 0, 1, 2, 3];
    const tiles = $$('.vpg-tile', sec).map((el, i) => ({
      el, i, job: el.getAttribute('data-job'), x: 0, y: 0, s: 1, o: 0,
      home: { x: 0, y: 0, s: 1, o: 1, z: 0 }, dest: { kind: 'home' }, tw: null, arrived: true,
      ph: hash(i * 3.1, i * 1.7) * 6.283
    }));
    const byJob = {};
    tiles.forEach((t) => { byJob[t.job] = t; });
    keys.forEach((k) => setLegend(k, '', false));
    const current = [null, null, null, null, null];
    let custom = null, sel = 0, play = false;

    let W = 0, H = 0, cx = 0, cy = 0, dw = 0, dh = 0, TS = 96, keyPos = [];
    let p = 0, sp = 0, visible = false, revealAt = 0, dpr = 1;
    let mx = 0, my = 0, pmx = 0, pmy = 0;

    function layout() {
      W = pin.clientWidth; H = pin.clientHeight;
      dw = devWrap.offsetWidth; dh = devWrap.offsetHeight;
      cx = devWrap.offsetLeft; cy = devWrap.offsetTop;
      const left = cx - dw / 2, top = cy - dh / 2;
      keyPos = KEYBOX.map((b) => ({ x: left + (b[0] + b[2] / 2) / 100 * dw, y: top + (b[1] + b[3] / 2) / 100 * dh, w: b[2] / 100 * dw, h: b[3] / 100 * dh }));
      glow.style.setProperty('--gx', cx + 'px');
      glow.style.setProperty('--gy', cy + 'px');
      const vis = tiles.filter((t) => t.el.offsetParent !== null);
      TS = vis.length ? vis[0].el.offsetWidth : 96;
      placeHomes(vis);
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.round(W * dpr);
      canvas.height = Math.round(H * dpr);
      drawn = -1;
    }

    function placeHomes(vis) {
      const n = vis.length;
      if (!n) return;
      const pr = pin.getBoundingClientRect();
      const cr = copy.getBoundingClientRect();
      const wide = W >= 990;
      /* keep tiles off the captions: the caption column on wide screens,
         the band above the tallest caption on narrow ones */
      let capH = 0;
      caps.forEach((c) => { capH = Math.max(capH, c.offsetHeight); });
      const ex = wide
        ? { r: cr.right - pr.left + TS * .35, t: 1e4 }
        : { r: -1e4, t: cr.bottom - pr.top - capH - TS * .3 };
      const navH = W >= 750 ? 68 : 60;
      const m = TS * .6 + 8;
      const top = navH + TS * .62 + 10;
      const pad = TS * .68;
      const g = Math.max(10, TS * .2);
      const cand = [];
      for (let y = top; y <= H - m; y += g) {
        for (let x = m; x <= W - m; x += g) {
          if (wide && x - TS * .5 < ex.r) continue;
          if (!wide && y + TS * .5 > ex.t) continue;
          if (Math.abs(x - cx) < dw / 2 + pad && Math.abs(y - cy) < dh / 2 + pad) continue;
          cand.push({ x, y, d: Math.hypot((x - cx) / (dw * .5 + TS), (y - cy) / (dh * .5 + TS)), n: hash(x * .37, y * .71) });
        }
      }
      const extras = vis.filter((t) => t.el.classList.contains('vpg-tile--extra'));
      const order = vis.filter((t) => extras.indexOf(t) < 0).concat(extras);
      const need = n - extras.length;
      let picked = [];
      /* spread the spots out (farthest-point sampling with a hard minimum gap, so
         tiles never overlap even while they bob); loosen the gap only if needed */
      for (const f of [1.3, 1.18, 1.06]) {
        const minD = TS * f + 6;
        picked = [];
        while (picked.length < n) {
          let best = null, bs = -1e9;
          for (const c of cand) {
            let md = 1e9;
            for (const q of picked) {
              const dd = Math.hypot(c.x - q.x, c.y - q.y);
              if (dd < md) { md = dd; if (md < minD) break; }
            }
            if (md < minD) continue;
            const sc = Math.min(md, TS * 2.5) - c.d * TS * .55 + c.n * TS * .3;
            if (sc > bs) { bs = sc; best = c; }
          }
          if (!best) break;
          picked.push(best);
        }
        if (picked.length >= need) break;
      }
      /* nearest spots go to the jobs the setups use; extra jobs only if there is room */
      const spots = picked.slice().sort((a, b) => a.d - b.d);
      order.forEach((t, i) => {
        let c = spots[i];
        /* no room left (extras first, then anything on very small screens): the tile stays hidden at home */
        t.off = !c;
        if (!c) c = { x: cx + Math.cos(i * 2.4) * (dw * .5 + TS * .9), y: cy + Math.sin(i * 2.4) * (dh * .5 + TS * .3), d: 2.2 };
        const z = c.d < 1.4 ? 0 : c.d < 2.1 ? 1 : 2;
        t.home = { x: c.x, y: c.y, s: [1, .9, .8][z], o: t.off ? 0 : [1, .88, .7][z], z };
        if (!t.tw && t.dest.kind === 'home') { t.x = c.x; t.y = c.y; t.s = t.home.s; }
      });
    }

    function homePos(t, now) {
      const h = t.home, ts = now / 1000;
      const par = (1 - h.z * .35);
      const amp = play ? .45 : 1;
      return {
        x: h.x + Math.sin(ts * .55 + t.ph) * TS * .07 * amp + pmx * 16 * par,
        y: h.y + Math.cos(ts * .47 + t.ph * 1.3) * TS * .09 * amp + pmy * 12 * par,
        s: h.s, o: h.o
      };
    }
    function keyTarget(k) {
      const kp = keyPos[k];
      return { x: kp.x, y: kp.y, s: Math.min(kp.w, kp.h) / TS * .96, o: 0 };
    }
    function retarget(t, dest, now) {
      const to = dest.kind === 'key' ? keyTarget(dest.k) : homePos(t, now);
      const dx = to.x - t.x, dy = to.y - t.y, dist = Math.hypot(dx, dy) || 1;
      const side = hash(t.i + 1, (dest.k || 0) + 7) > .5 ? 1 : -1;
      t.tw = { fx: t.x, fy: t.y, fs: t.s, fo: t.o, t0: now, dur: 640 + Math.min(560, dist * .55), cx: -dy / dist * dist * .2 * side, cy: dx / dist * dist * .2 * side };
      t.dest = dest;
      t.arrived = false;
    }
    function land(k, job) {
      setLegend(keys[k], job || '', true);
      if (job) hit(keys[k]);
    }
    function mappingAt(q) {
      if (q >= END) { if (!custom) custom = setups[N - 1].slice(); return custom; }
      const m = [null, null, null, null, null];
      for (let i = 0; i < N; i++) {
        const st = S0 + i * SEG;
        for (let j = 0; j < 5; j++) {
          const k = ORDER[j];
          if (q >= st + j * STAG) m[k] = setups[i][k] || null;
        }
      }
      return m;
    }
    function applyMapping(m, now) {
      for (let k = 0; k < 5; k++) {
        if (m[k] === current[k]) continue;
        const old = current[k];
        current[k] = m[k];
        if (m[k]) {
          const t = byJob[m[k]];
          if (t && t.el.offsetParent !== null) retarget(t, { kind: 'key', k }, now);
          else land(k, m[k]);
        } else {
          land(k, '');
        }
        if (old && m.indexOf(old) < 0) {
          const t = byJob[old];
          if (t && !(t.dest.kind === 'key' && m[t.dest.k] === old)) retarget(t, { kind: 'home' }, now);
        }
      }
    }
    function stepTile(t, now) {
      const dest = t.dest;
      const to = dest.kind === 'key' ? keyTarget(dest.k) : homePos(t, now);
      let intro = 1;
      if (revealAt) intro = easeIO(clamp((now - revealAt - t.i * 45) / 700, 0, 1));
      if (!t.tw) {
        t.x = to.x; t.y = to.y; t.s = to.s; t.o = to.o;
      } else {
        const tw = t.tw;
        const u = clamp((now - tw.t0) / tw.dur, 0, 1);
        const e = easeIO(u);
        const mx2 = (tw.fx + to.x) / 2 + tw.cx, my2 = (tw.fy + to.y) / 2 + tw.cy;
        const a = (1 - e) * (1 - e), b = 2 * (1 - e) * e, c = e * e;
        t.x = a * tw.fx + b * mx2 + c * to.x;
        t.y = a * tw.fy + b * my2 + c * to.y;
        t.s = tw.fs + (to.s - tw.fs) * e;
        if (dest.kind === 'key') {
          t.o = u < .78 ? Math.min(1, tw.fo + (1 - tw.fo) * Math.min(1, u / .16)) : Math.max(0, 1 - (u - .78) / .18);
          if (!t.arrived && u >= .86) { t.arrived = true; land(dest.k, t.job); }
        } else {
          t.o = tw.fo + (to.o - tw.fo) * Math.min(1, u / .3);
        }
        if (u >= 1) t.tw = null;
      }
      const s = t.s * (dest.kind === 'home' ? (.55 + .45 * intro) : 1);
      const o = t.o * (dest.kind === 'home' ? intro : 1);
      t.el.style.transform = 'translate3d(' + (t.x - TS / 2).toFixed(1) + 'px,' + (t.y - TS / 2).toFixed(1) + 'px,0) scale(' + s.toFixed(3) + ')';
      t.el.style.opacity = o.toFixed(3);
      t.el.style.zIndex = dest.kind === 'key' ? 5 : 3 - t.home.z;
      const parked = !t.tw && ((t.off && dest.kind === 'home') || dest.kind === 'key');
      t.el.style.visibility = parked ? 'hidden' : '';
    }

    /* film frames */
    const data = JSON.parse(($('[data-frames]', sec) || { textContent: '{}' }).textContent || '{}');
    let imgs = [], ready = [], setName = '', drawn = -1, filmOn = true;
    function loadFrames() {
      const portrait = (pin.clientWidth / Math.max(1, pin.clientHeight)) < .9;
      const name = portrait ? 'm' : 'd';
      const urls = data[name] || [];
      if (name === setName || !urls.length) return;
      setName = name;
      ready = urls.map(() => false);
      imgs = urls.map((u, i) => {
        const im = new Image();
        im.decoding = 'async';
        im.src = u;
        /* decode off the main thread before the first draw */
        const ok = () => { ready[i] = true; drawn = -1; };
        if (im.decode) im.decode().then(ok, () => { im.onload = ok; if (im.complete && im.naturalWidth) ok(); });
        else im.onload = ok;
        return im;
      });
    }
    function drawFrame(idx) {
      if (!imgs.length) return;
      let use = -1;
      for (let d = 0; d < imgs.length; d++) {
        if (idx - d >= 0 && ready[idx - d]) { use = idx - d; break; }
        if (idx + d < imgs.length && ready[idx + d]) { use = idx + d; break; }
      }
      if (use < 0 || use === drawn) return;
      const im = imgs[use];
      const cw = canvas.width, ch = canvas.height;
      const sc = Math.max(cw / im.naturalWidth, ch / im.naturalHeight);
      const w = im.naturalWidth * sc, h = im.naturalHeight * sc;
      cctx.drawImage(im, (cw - w) / 2, (ch - h) / 2, w, h);
      if (drawn < 0) film.style.backgroundImage = 'none';
      drawn = use;
    }

    function rawProgress() {
      const r = sec.getBoundingClientRect();
      const total = r.height - pin.offsetHeight;
      return total > 0 ? clamp(-r.top / total, 0, 1) : 0;
    }
    function capOpacity(q, a, b, last) {
      const fi = smooth((q - a) / .03);
      const fo = last ? 1 : 1 - smooth((q - (b - .03)) / .03);
      return fi * fo;
    }
    function setPlay(on) {
      if (on === play) return;
      play = on;
      sec.classList.toggle('is-play', on);
      tilesBox.setAttribute('aria-hidden', on ? 'false' : 'true');
      tiles.forEach((t) => {
        t.el.tabIndex = on ? 0 : -1;
        if (on) t.el.setAttribute('data-cursor', 'Assign'); else t.el.removeAttribute('data-cursor');
      });
      keys.forEach((k, i) => {
        k.tabIndex = on ? 0 : -1;
        k.classList.toggle('is-sel', on && i === sel);
        k.setAttribute('aria-pressed', on && i === sel ? 'true' : 'false');
        if (on) k.setAttribute('data-cursor', 'Select'); else k.removeAttribute('data-cursor');
      });
    }

    function tick(now, dt) {
      p = rawProgress();
      const f = 1 - Math.pow(1 - .16, dt / 16.667);
      sp += (p - sp) * f;
      if (Math.abs(p - sp) < .00005) sp = p;
      const q = sp;

      /* film */
      const filmO = 1 - smooth((q - (F - .05)) / .04);
      if (filmO > .002) {
        if (!filmOn) { film.style.visibility = 'visible'; filmOn = true; }
        film.style.opacity = filmO.toFixed(3);
        const idx = Math.round(clamp(q / (F * .92), 0, 1) * (Math.max(1, imgs.length) - 1));
        drawFrame(idx);
        canvas.style.transform = 'scale(' + (1 + .07 * clamp(q / F, 0, 1)).toFixed(4) + ')';
      } else if (filmOn) { film.style.visibility = 'hidden'; filmOn = false; }
      const o1 = 1 - smooth((q - (F - .06)) / .035);
      t1.style.opacity = o1.toFixed(3);
      t1.style.transform = 'translate3d(0,' + (-(1 - o1) * 40).toFixed(1) + 'px,0)';
      const o2 = smooth((q - .03) / .035) * (1 - smooth((q - (F - .055)) / .035));
      t2.style.opacity = o2.toFixed(3);
      t2.style.transform = 'translate3d(0,' + ((1 - smooth((q - .03) / .035)) * 22 - (1 - (1 - smooth((q - (F - .055)) / .035))) * 30).toFixed(1) + 'px,0)';
      const tone = q < F - .02 ? 'light' : 'dark';
      if (sec.getAttribute('data-vpg-tone') !== tone) { sec.setAttribute('data-vpg-tone', tone); if (Nav.refresh) Nav.refresh(); }

      /* stage */
      const so = smooth((q - (F - .004)) / .045);
      if (so > .001) {
        if (stage.style.visibility !== 'visible') {
          stage.style.visibility = 'visible';
          if (!revealAt) revealAt = now;
        }
        stage.style.opacity = so.toFixed(3);
        devWrap.style.transform = 'translate(-50%,-50%) scale(' + (.88 + .12 * easeIO(clamp((q - (F - .004)) / .08, 0, 1))).toFixed(4) + ')';
      } else if (stage.style.visibility === 'visible') {
        stage.style.visibility = 'hidden';
        stage.style.opacity = '0';
        revealAt = 0;
      }
      sec.classList.toggle('is-stage', q >= F);

      /* setups */
      const m = q < S0 ? [null, null, null, null, null] : mappingAt(q);
      applyMapping(m, now);
      setPlay(q >= END + .01);

      /* captions and dots */
      let active = -1;
      setupCaps.forEach((c, i) => {
        const a = S0 + i * SEG - .006, b = i === N - 1 ? END : S0 + (i + 1) * SEG - .006;
        const o = capOpacity(q, a, b, false);
        if (o > .5) active = i;
        c.style.opacity = o.toFixed(3);
        c.style.transform = (W >= 990 ? 'translateY(calc(-50% + ' : 'translateY(calc(0% + ') + ((1 - o) * (q < (a + b) / 2 ? 26 : -26)).toFixed(1) + 'px))';
        c.style.visibility = o > .002 ? 'visible' : 'hidden';
      });
      const endCap = caps[caps.length - 1];
      if (endCap && endCap.getAttribute('data-cap') === 'end') {
        const o = capOpacity(q, END, 1.1, true);
        if (o > .5) active = N;
        endCap.style.opacity = o.toFixed(3);
        endCap.style.transform = (W >= 990 ? 'translateY(calc(-50% + ' : 'translateY(calc(0% + ') + ((1 - o) * 26).toFixed(1) + 'px))';
        endCap.style.visibility = o > .002 ? 'visible' : 'hidden';
      }
      dots.forEach((d, i) => d.classList.toggle('is-on', i === active));

      /* pointer parallax */
      pmx += (mx - pmx) * .06; pmy += (my - pmy) * .06;

      if (stage.style.visibility === 'visible') tiles.forEach((t) => stepTile(t, now));
    }

    /* the playable end: pick a key, then a job */
    keys.forEach((k, i) => k.addEventListener('click', () => {
      if (!play) return;
      sel = i;
      keys.forEach((kk, j) => { kk.classList.toggle('is-sel', j === i); kk.setAttribute('aria-pressed', j === i ? 'true' : 'false'); });
      hit(k);
    }));
    tiles.forEach((t) => t.el.addEventListener('click', () => {
      if (!play || !custom) return;
      const at = custom.indexOf(t.job);
      if (at === sel) { hit(keys[sel]); return; }
      const prev = custom[sel];
      if (at >= 0) custom[at] = prev;
      custom[sel] = t.job;
    }));
    if (FINE) {
      sec.addEventListener('pointermove', (e) => {
        if (e.pointerType !== 'mouse') return;
        mx = e.clientX / window.innerWidth - .5; my = e.clientY / window.innerHeight - .5;
      }, { passive: true });
    }

    let ro = null;
    const relayout = () => { layout(); if (setName) { const pr = (pin.clientWidth / Math.max(1, pin.clientHeight)) < .9 ? 'm' : 'd'; if (pr !== setName) loadFrames(); } };
    if ('ResizeObserver' in window) { ro = new ResizeObserver(relayout); ro.observe(pin); } else window.addEventListener('resize', relayout);
    layout();
    tiles.forEach((t) => { t.x = t.home.x; t.y = t.home.y; t.s = t.home.s; t.o = t.home.o; });
    const io1 = onScreen(sec, loadFrames, null, 0, '200% 0px 200% 0px');
    const io2 = onScreen(sec, () => { if (!visible) { visible = true; sp = rawProgress(); Ticker.add(tick); } }, () => { visible = false; Ticker.remove(tick); }, 0, '60px 0px 60px 0px');
    return { destroy() { Ticker.remove(tick); if (io1) io1.disconnect(); if (io2) io2.disconnect(); if (ro) ro.disconnect(); } };
  }

  /* ======================================================= your AI */
  const AI_PROMPTS = [
    'Draft a friendly reply saying Thursday at 3 works for me.',
    'Summarize this contract and list the three biggest risks.',
    'Plan three days in Kyoto with one great dinner each night.',
    'What are people saying about the launch today?'
  ];
  function AiDemo(sec) {
    const box = $('[data-vpg-demo="ai"]', sec);
    if (!box) return null;
    const keys = $$('.vpg-aikey', sec), name = $('[data-ai-name]', box), opened = $('[data-opened]', box);
    const hello = $('[data-hello]', box), msg = $('[data-msg]', box), sent = $('[data-sent]', box), reply = $('[data-reply]', box);
    const input = $('[data-input]', box), typed = $('[data-typed]', box);
    const names = keys.map((k) => $('.vpg-aikey__name', k).textContent.trim());
    let at = 0;
    function clearChat() {
      hello.classList.remove('is-gone'); msg.classList.remove('is-on'); reply.classList.remove('is-on');
      typed.textContent = '';
      input.classList.remove('is-live', 'is-typing', 'is-ready');
    }
    function reset() {
      clearChat();
      keys.forEach((k) => { k.classList.remove('is-lit'); k.setAttribute('aria-pressed', 'false'); });
      opened.classList.remove('is-on');
    }
    async function show(tok, i) {
      keys.forEach((k, j) => { k.classList.toggle('is-lit', j === i); k.setAttribute('aria-pressed', j === i ? 'true' : 'false'); });
      opened.classList.remove('is-on');
      name.classList.add('is-swap');
      await wait(tok, 200);
      clearChat();
      name.textContent = names[i];
      name.classList.remove('is-swap');
      await wait(tok, 250);
      opened.classList.add('is-on');
      await wait(tok, 420);
      input.classList.add('is-live', 'is-typing');
      await typeText(tok, typed, AI_PROMPTS[i % AI_PROMPTS.length], 25);
      input.classList.add('is-ready');
      await wait(tok, 480);
      input.classList.remove('is-live', 'is-typing', 'is-ready');
      sent.textContent = typed.textContent;
      typed.textContent = '';
      hello.classList.add('is-gone');
      msg.classList.add('is-on');
      await wait(tok, 520);
      reply.classList.add('is-on');
      await wait(tok, 2200);
      keys[i].classList.remove('is-lit');
    }
    async function script(tok) { await show(tok, at); at = (at + 1) % keys.length; await wait(tok, 240); }
    function still(i) {
      reset();
      keys[i].classList.add('is-lit'); keys[i].setAttribute('aria-pressed', 'true');
      name.textContent = names[i]; opened.classList.add('is-on');
      sent.textContent = AI_PROMPTS[i % AI_PROMPTS.length];
      hello.classList.add('is-gone'); msg.classList.add('is-on'); reply.classList.add('is-on');
    }
    const demo = Looping(box, reset, script, () => still(0));
    keys.forEach((k, i) => k.addEventListener('click', () => { at = i; if (REDUCE) still(i); else demo.restart(); }));
    return demo;
  }

  /* ======================================================= Genspark */
  const GS_RUNS = [
    { cmd: 'Make a 6-slide deck on our Q3 results.', out: 'deck', steps: ['Planning the outline', 'Gathering the numbers', 'Building the charts', 'Designing the slides'] },
    { cmd: 'Compare our top five competitors in a sheet.', out: 'sheet', steps: ['Finding the competitors', 'Collecting prices and ratings', 'Building the table', 'Writing a summary'] }
  ];
  function GsDemo(sec) {
    const box = $('[data-vpg-demo="genspark"]', sec);
    if (!box) return null;
    const cmd = $('[data-cmd]', box), go = $('[data-go]', box), steps = $$('[data-step]', box), outs = $$('[data-out]', box), kinds = $$('[data-kind]', box);
    let n = 0;
    function reset() {
      box.classList.remove('is-live', 'is-working');
      cmd.textContent = '';
      go.classList.remove('is-on', 'is-press');
      steps.forEach((s, i) => { s.classList.remove('is-run', 'is-done'); $('[data-step-text]', s).textContent = GS_RUNS[n].steps[i]; });
      outs.forEach((o) => o.classList.remove('is-on'));
      kinds.forEach((k) => k.classList.toggle('is-on', k.getAttribute('data-kind') === GS_RUNS[n].out));
    }
    async function script(tok) {
      const run = GS_RUNS[n];
      await wait(tok, 600);
      box.classList.add('is-live');
      await typeText(tok, cmd, run.cmd, 30);
      box.classList.remove('is-live');
      go.classList.add('is-on');
      await wait(tok, 360);
      go.classList.add('is-press');
      await wait(tok, 160);
      go.classList.remove('is-press');
      box.classList.add('is-working');
      for (let i = 0; i < steps.length; i++) {
        steps[i].classList.add('is-run');
        await wait(tok, 620 + i * 60);
        steps[i].classList.remove('is-run');
        steps[i].classList.add('is-done');
      }
      box.classList.remove('is-working');
      outs.forEach((o) => o.classList.toggle('is-on', o.getAttribute('data-out') === run.out));
      await wait(tok, 3800);
      n = (n + 1) % GS_RUNS.length;
    }
    return Looping(box, reset, script, () => {
      cmd.textContent = GS_RUNS[0].cmd; go.classList.add('is-on');
      steps.forEach((s) => s.classList.add('is-done'));
      outs.forEach((o) => o.classList.toggle('is-on', o.getAttribute('data-out') === 'deck'));
    });
  }

  /* ===================================================== top bar */
  function Nav() {
    const bar = $('[data-vpg-nav]');
    if (!bar || bar._vpg) return;
    bar._vpg = 1;
    const prog = $('.vpg-nav__progress', bar);
    let on = false, tone = '', queued = false;
    function check() {
      queued = false;
      const y = window.scrollY || window.pageYOffset;
      const want = y > 8;
      if (want !== on) { on = want; bar.classList.toggle('is-solid', on); }
      const mid = bar.offsetHeight / 2;
      /* the last match wins: later sections slide over earlier ones (the sheets) */
      let t = '';
      $$('[data-vpg-tone]').forEach((s) => {
        const r = s.getBoundingClientRect();
        if (r.top <= mid && r.bottom > mid) t = s.getAttribute('data-vpg-tone');
      });
      if (t !== tone) {
        bar.classList.toggle('is-dark', t === 'dark');
        bar.classList.toggle('is-blue', t === 'blue');
        tone = t;
      }
      if (prog) {
        const max = Math.max(1, root.scrollHeight - window.innerHeight);
        prog.style.setProperty('--p', clamp(y / max, 0, 1).toFixed(4));
      }
    }
    const queue = () => { if (!queued) { queued = true; requestAnimationFrame(check); } };
    window.addEventListener('scroll', queue, { passive: true });
    window.addEventListener('resize', queue);
    Ticker.add(function watch() { check(); Ticker.remove(watch); });
    check();
    Nav.refresh = queue;
  }

  /* ======================================== pointer label (desktop) */
  function Cursor() {
    if (!FINE || REDUCE || $('.vpg-cursor')) return;
    const el = doc.createElement('div');
    el.className = 'vpg-cursor';
    el.setAttribute('aria-hidden', 'true');
    el.innerHTML = '<span></span>';
    doc.body.appendChild(el);
    const label = el.firstChild;
    let x = 0, y = 0, tx = 0, ty = 0, on = false, running = false;
    function tick() {
      x += (tx - x) * .35; y += (ty - y) * .35;
      el.style.transform = 'translate3d(' + x.toFixed(1) + 'px,' + y.toFixed(1) + 'px,0)';
      if (!on && Math.abs(tx - x) < .3 && Math.abs(ty - y) < .3) { Ticker.remove(tick); running = false; }
    }
    doc.addEventListener('pointermove', (e) => {
      if (e.pointerType !== 'mouse') return;
      tx = e.clientX; ty = e.clientY;
      const t = e.target && e.target.closest ? e.target.closest('[data-cursor]') : null;
      const txt = t && t.getAttribute('data-cursor');
      if (txt) {
        if (label.textContent !== txt) label.textContent = txt;
        if (!on) { on = true; x = tx; y = ty; el.classList.add('is-on'); }
        el.classList.toggle('is-dark', !!t.closest('.vpg-dark, .vpg-ch, .vpg-ai'));
      } else if (on) { on = false; el.classList.remove('is-on'); }
      if (!running) { running = true; Ticker.add(tick); }
    }, { passive: true });
    doc.addEventListener('pointerleave', () => { on = false; el.classList.remove('is-on'); });
  }

  /* ============================================================ wiring */
  let registry = [];
  /* ========================================================= a day of scenes
     Each scene plays when it reaches the middle of the screen: the key goes
     down, the words appear as said (muted, ums and all), get cleaned up, and
     land in the app. The clock rolls to the scene's time and the page turns
     from morning white to night black as you scroll. */
  const DAY_STOPS = [[255, 255, 255], [245, 248, 255], [233, 241, 255], [216, 231, 255], [10, 34, 84], [0, 0, 0]];
  function dayRGB(t) {
    const n = DAY_STOPS.length - 1, x = clamp(t, 0, 1) * n, i = Math.min(n - 1, Math.floor(x)), f = smooth(x - i);
    const a = DAY_STOPS[i], b = DAY_STOPS[i + 1];
    return [0, 1, 2].map((k) => Math.round(a[k] + (b[k] - a[k]) * f));
  }
  const normWord = (w) => w.toLowerCase().replace(/[^0-9a-zÀ-ɏ%]+/g, '');
  /* line up the words as said with the clean words: kept, dropped, added */
  function wordDiff(raw, clean) {
    const A = raw.split(/\s+/).filter(Boolean), B = clean.split(/\s+/).filter(Boolean);
    const a = A.map(normWord), b = B.map(normWord);
    const L = [];
    for (let i = 0; i <= A.length; i++) L.push(new Array(B.length + 1).fill(0));
    for (let i = A.length - 1; i >= 0; i--) {
      for (let j = B.length - 1; j >= 0; j--) L[i][j] = a[i] && a[i] === b[j] ? L[i + 1][j + 1] + 1 : Math.max(L[i + 1][j], L[i][j + 1]);
    }
    const out = [];
    let i = 0, j = 0, same = 0;
    while (i < A.length || j < B.length) {
      if (i < A.length && j < B.length && a[i] && a[i] === b[j]) { out.push({ r: A[i], c: B[j] }); i++; j++; same++; }
      else if (i < A.length && (j >= B.length || L[i + 1][j] >= L[i][j + 1])) { out.push({ r: A[i], c: null }); i++; }
      else { out.push({ r: null, c: B[j] }); j++; }
    }
    return { tokens: out, sim: same / Math.max(1, Math.max(A.length, B.length)) };
  }
  function Speech(host, raw, clean) {
    const line = doc.createElement('span');
    line.className = 'vpg-scene__line';
    line.setAttribute('aria-hidden', 'true');
    const sr = doc.createElement('span');
    sr.className = 'vpg-sr';
    sr.textContent = clean;
    const old = host.querySelector('.vpg-scene__line');
    if (old) old.replaceWith(line); else host.appendChild(line);
    host.insertBefore(sr, line);
    const d = wordDiff(raw, clean);
    const sp = doc.createElement('span');
    let toks = [];
    const add = (cls, html) => { const s = doc.createElement('span'); s.className = 'vpg-tk ' + cls; s.innerHTML = html; sp.appendChild(s); return s; };
    const esc = (t) => t.replace(/[&<>]/g, (m) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[m]));
    const qa = doc.createElement('span'), qb = doc.createElement('span');
    qa.className = qb.className = 'vpg-q';
    qa.textContent = '“'; qb.textContent = '”';
    line.appendChild(qa);
    const quotes = (a, b) => { qa.classList.toggle('is-wait', !a); qb.classList.toggle('is-wait', !b); };
    if (d.sim < .3) {
      /* another language: the whole line is swapped */
      sp.className = 'vpg-speech vpg-speech--two is-raw';
      const r = doc.createElement('span'), c = doc.createElement('span');
      line.removeChild(qa);
      r.appendChild(qa);
      raw.split(/\s+/).forEach((w, k, arr) => { const s = doc.createElement('span'); s.className = 'vpg-tk is-said'; s.textContent = w + (k < arr.length - 1 ? ' ' : ''); r.appendChild(s); toks.push({ el: s, kind: 'said' }); });
      r.appendChild(qb);
      c.textContent = '“' + clean + '”';
      sp.appendChild(r); sp.appendChild(c);
      line.appendChild(sp);
      return {
        reset() { sp.classList.remove('is-clean'); sp.classList.add('is-raw'); toks.forEach((t) => t.el.classList.add('is-wait')); quotes(false, false); },
        async say(tok, ms) { quotes(true, false); for (const t of toks) { t.el.classList.remove('is-wait'); await wait(tok, ms); } quotes(true, true); },
        clean() { sp.classList.remove('is-raw'); sp.classList.add('is-clean'); quotes(true, true); },
        final() { toks.forEach((t) => t.el.classList.remove('is-wait')); this.clean(); }
      };
    }
    sp.className = 'vpg-speech is-raw';
    d.tokens.forEach((t, k) => {
      const tail = k < d.tokens.length - 1 ? ' ' : '';
      if (t.r != null && t.c != null) {
        if (t.r === t.c) toks.push({ el: add('is-same', esc(t.r + tail)), kind: 'same' });
        else toks.push({ el: add('is-swap', '<span class="vpg-tk__s"><span>' + esc(t.r + tail) + '</span><span>' + esc(t.c + tail) + '</span></span>'), kind: 'swap' });
      } else if (t.r != null) toks.push({ el: add('is-drop', esc(t.r + tail)), kind: 'drop' });
      else toks.push({ el: add('is-add', esc(t.c + tail)), kind: 'add' });
    });
    line.appendChild(sp);
    line.appendChild(qb);
    /* widths in em, so they still fit when a whispered line grows to full size */
    const widths = () => toks.forEach((t) => { const fs = parseFloat(getComputedStyle(t.el).fontSize) || 16; t.w = t.el.scrollWidth / fs; t.ow = t.el.offsetWidth / fs; });
    return {
      reset() {
        sp.classList.add('is-raw');
        quotes(false, false);
        toks.forEach((t) => {
          t.el.classList.remove('is-gone', 'is-strike', 'is-in', 'is-swapped', 'is-settled');
          t.el.style.maxWidth = '';
          if (t.kind !== 'add') t.el.classList.add('is-wait');
          if (t.kind === 'add') t.el.style.maxWidth = '0px';
        });
      },
      async say(tok, ms) {
        quotes(true, false);
        for (const t of toks) {
          if (t.kind === 'add') continue;
          t.el.classList.remove('is-wait');
          await wait(tok, ms);
        }
        quotes(true, true);
      },
      clean() {
        sp.classList.remove('is-raw');
        widths();
        toks.forEach((t) => {
          if (t.kind === 'drop') { t.el.style.maxWidth = t.ow + 'em'; t.el.classList.add('is-strike'); }
        });
        requestAnimationFrame(() => {
          toks.forEach((t, k) => {
            if (t.kind === 'drop') setTimeout(() => { t.el.classList.add('is-gone'); t.el.style.maxWidth = '0px'; }, 120);
            if (t.kind === 'add') { t.el.style.maxWidth = (t.w || 6) + 'em'; t.el.classList.add('is-in'); setTimeout(() => { t.el.style.maxWidth = ''; }, 600); }
            if (t.kind === 'swap') { setTimeout(() => t.el.classList.add('is-swapped'), 40 + k * 12); setTimeout(() => t.el.classList.add('is-settled'), 520 + k * 12); }
          });
          setTimeout(() => toks.forEach((t) => { if (t.kind === 'drop') t.el.style.display = 'none'; }), 620);
        });
      },
      final() {
        sp.classList.remove('is-raw');
        quotes(true, true);
        toks.forEach((t) => {
          t.el.classList.remove('is-wait', 'is-strike');
          t.el.style.maxWidth = '';
          if (t.kind === 'drop') { t.el.classList.add('is-gone'); t.el.style.display = 'none'; }
          if (t.kind === 'add') t.el.classList.add('is-in');
          if (t.kind === 'swap') t.el.classList.add('is-swapped', 'is-settled');
        });
      },
      show() { toks.forEach((t) => { if (t.kind === 'drop') t.el.style.display = ''; }); }
    };
  }

  /* the rolling clock: hours, a colon, minutes */
  function Odometer(el) {
    const small = el.querySelector('small');
    const ampm = small ? small.textContent : '';
    el.textContent = '';
    const slot = (vals) => {
      const o = doc.createElement('span'); o.className = 'vpg-od';
      const s = doc.createElement('span'); s.className = 'vpg-od__s';
      vals.forEach((v) => { const i = doc.createElement('i'); i.textContent = v; s.appendChild(i); });
      o.appendChild(s); el.appendChild(o);
      return { o, s, vals };
    };
    const digits = '0123456789'.split('');
    const h1 = slot([' ', '1']), h2 = slot(digits);
    const colon = doc.createElement('span'); colon.className = 'vpg-od__c'; colon.textContent = ':'; el.appendChild(colon);
    const m1 = slot(digits.slice(0, 6)), m2 = slot(digits);
    const am = doc.createElement('small'); am.textContent = ampm; el.appendChild(am);
    let h1w = 0;
    function set(time, ap, instant) {
      const m = /^(\d{1,2}):(\d{2})$/.exec((time || '').trim());
      if (!m) return;
      const hh = m[1].length === 2 ? m[1] : ' ' + m[1];
      const go = (sl, v) => {
        const i = Math.max(0, sl.vals.indexOf(v));
        if (instant) { sl.s.style.transition = 'none'; requestAnimationFrame(() => { sl.s.style.transition = ''; }); }
        sl.s.style.transform = 'translateY(' + (-i) + 'em)';
      };
      if (!h1w) h1w = h1.o.offsetWidth || 0;
      h1.o.style.width = hh[0] === '1' ? (h1w ? h1w + 'px' : '') : '0px';
      go(h1, hh[0]); go(h2, hh[1]); go(m1, m[2][0]); go(m2, m[2][1]);
      am.textContent = ap || '';
    }
    return { set };
  }

  function Day(sec) {
    const scenes = $$('[data-scene]', sec).map((el) => ({
      el,
      time: el.getAttribute('data-time'), ampm: el.getAttribute('data-ampm'), place: el.getAttribute('data-place'),
      key: clamp((parseInt(el.getAttribute('data-key'), 10) || 5) - 1, 0, 4), press: el.getAttribute('data-press') || 'tap',
      job: el.getAttribute('data-job') || '', card: el.getAttribute('data-card'), quiet: el.hasAttribute('data-quiet'),
      cardEl: $('[data-card-el]', el), steps: $$('[data-step]', el), types: $$('[data-type]', el)
    }));
    if (!scenes.length) return null;
    const clockEl = $('[data-clock]', sec), placeEl = $('[data-place]', sec), howEl = $('[data-how]', sec), track = $('[data-track]', sec), pad = $('[data-pad]', sec);
    const dev = $('.vpg-day__dev .vpg-dev', sec);
    const keys = dev ? $$('.vpg-key', dev) : [];
    const padKeys = pad ? $$('.vpg-pad__key', pad) : [];
    const clock = clockEl ? Odometer(clockEl) : null;
    scenes.forEach((s) => {
      const said = $('.vpg-scene__said', s.el);
      s.speech = Speech(said, said.getAttribute('data-said') || '', said.getAttribute('data-lands') || '');
      s.text = s.types.map((t) => t.textContent);
      s.el.classList.add('is-pre');
      if (REDUCE) { s.speech.final(); s.el.classList.remove('is-pre'); }
      else s.speech.reset();
    });
    const run = new Runner();
    const rail = $('.vpg-day__rail', sec);
    let active = -1, visible = false, dark = false, lastCol = '', lastT = -1, queued = false;
    if (clock) clock.set(scenes[0].time, scenes[0].ampm, true);

    function lightKey(k, down) {
      keys.forEach((el, i) => { el.classList.toggle('is-lit', i === k); el.classList.toggle('is-down', i === k && !!down); el.setAttribute('aria-pressed', i === k ? 'true' : 'false'); });
      padKeys.forEach((el, i) => el.classList.toggle('is-lit', i === k));
    }
    function setRail(s, instant) {
      if (clock) clock.set(s.time, s.ampm, instant);
      if (placeEl && placeEl.textContent !== s.place) {
        if (instant || REDUCE) placeEl.textContent = s.place;
        else { placeEl.classList.add('is-swap'); setTimeout(() => { placeEl.textContent = s.place; placeEl.classList.remove('is-swap'); }, 220); }
      }
      if (howEl) howEl.textContent = (s.press === 'hold' ? 'Hold' : 'Tap') + ' · ' + s.job;
    }
    async function typeInto(tok, el, text, speed) {
      const card = el.closest('.vpg-card');
      if (card) card.classList.add('is-typing');
      try { await typeText(tok, el, text, speed); } finally { if (card) card.classList.remove('is-typing'); }
    }
    async function play(s, tok) {
      s.el.classList.add('is-pre');
      s.steps.forEach((st) => st.classList.remove('is-on'));
      s.types.forEach((t) => { t.textContent = ''; });
      if (s.speech.show) s.speech.show();
      s.speech.reset();
      lightKey(s.key, s.press === 'hold');
      if (s.press === 'tap') { await wait(tok, 180); lightKey(s.key, false); }
      await wait(tok, 260);
      await s.speech.say(tok, s.quiet ? 95 : 80);
      await wait(tok, 380);
      if (s.press === 'hold') lightKey(s.key, false);
      s.speech.clean();
      await wait(tok, 520);
      const T = s.text[0] || '';
      if (s.card === 'email' || s.card === 'code') {
        if (s.types[0]) await typeInto(tok, s.types[0], T, s.card === 'code' ? 14 : 18);
        for (const st of s.steps) { await wait(tok, 380); st.classList.add('is-on'); }
      } else if (s.card === 'chat') {
        if (s.steps[0]) s.steps[0].classList.add('is-on');
        await wait(tok, 260);
        if (s.types[0]) await typeInto(tok, s.types[0], T, 18);
      } else if (s.card === 'notes') {
        for (let i = 0; i < s.steps.length; i++) {
          s.steps[i].classList.add('is-on');
          if (i === 0 && s.types[0]) await typeInto(tok, s.types[0], T, 14);
          await wait(tok, 320);
        }
      } else if (s.card === 'memo') {
        if (s.steps[0]) s.steps[0].classList.add('is-on');
        if (s.types[0]) await typeInto(tok, s.types[0], T, 16);
        for (let i = 1; i < s.steps.length; i++) { await wait(tok, 700); s.steps[i].classList.add('is-on'); }
      } else {
        for (const st of s.steps) { await wait(tok, 230); st.classList.add('is-on'); }
      }
      s.el.classList.remove('is-pre');
    }
    function finish(s) {
      s.speech.final();
      s.types.forEach((t, i) => { t.textContent = s.text[i]; });
      s.steps.forEach((st) => st.classList.add('is-on'));
      s.el.classList.remove('is-pre');
    }
    function activate(i, instant) {
      if (i === active) return;
      const prev = scenes[active];
      if (prev) { run.stop(); finish(prev); }
      active = i;
      const s = scenes[i];
      setRail(s, instant);
      if (REDUCE) { lightKey(s.key, false); finish(s); return; }
      const tok = run.token();
      play(s, tok).catch(swallow);
    }
    function tick() {
      const vh = window.innerHeight || 1, mid = vh * .5;
      const c = scenes.map((s) => { const r = s.el.getBoundingClientRect(); return r.top + r.height / 2; });
      let best = 0, bd = 1e9;
      c.forEach((y, i) => { const d = Math.abs(y - mid); if (d < bd) { bd = d; best = i; } });
      let x = 0;
      const n = c.length;
      if (mid <= c[0]) x = 0;
      else if (mid >= c[n - 1]) x = n - 1;
      else for (let i = 0; i < n - 1; i++) { if (c[i] <= mid && mid <= c[i + 1]) { x = i + (mid - c[i]) / Math.max(1, c[i + 1] - c[i]); break; } }
      const t = n > 1 ? x / (n - 1) : 0;
      const rgb = dayRGB(t), col = 'rgb(' + rgb.join(',') + ')';
      if (col !== lastCol) { lastCol = col; sec.style.backgroundColor = col; if (rail) rail.style.backgroundColor = col; }
      if (track && Math.abs(t - lastT) > .0005) { lastT = t; track.style.setProperty('--d', t.toFixed(4)); }
      const isDark = (rgb[0] * .2126 + rgb[1] * .7152 + rgb[2] * .0722) / 255 < .42;
      if (isDark !== dark) {
        dark = isDark;
        sec.classList.toggle('is-dark', dark);
        sec.setAttribute('data-vpg-tone', dark ? 'dark' : 'light');
        if (Nav.refresh) Nav.refresh();
      }
      const first = scenes[0].el.getBoundingClientRect();
      if (first.top < vh * .8 && bd < vh * .45) activate(best, active < 0);
    }
    keys.forEach((k, i) => {
      k.tabIndex = -1;
      k.addEventListener('click', () => {
        const s = scenes[active];
        if (s && s.key === i && !REDUCE) { run.stop(); const tok = run.token(); play(s, tok).catch(swallow); }
        else hit(k);
      });
      k.setAttribute('data-cursor', 'Press');
    });
    const queue = () => { if (visible && !queued) { queued = true; requestAnimationFrame(() => { queued = false; tick(); }); } };
    window.addEventListener('scroll', queue, { passive: true });
    window.addEventListener('resize', queue);
    const io = onScreen(sec, () => { if (!visible) { visible = true; queue(); } }, () => { visible = false; }, 0);
    return { destroy() { run.stop(); window.removeEventListener('scroll', queue); window.removeEventListener('resize', queue); if (io) io.disconnect(); } };
  }

  /* "Why not just use your laptop?": each row lights its key once it is in view */
  function Why(sec) {
    const rows = $$('.vpg-why__row', sec);
    if (REDUCE || !('IntersectionObserver' in window)) { rows.forEach((r) => r.classList.add('is-in')); return null; }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); } });
    }, { threshold: 0.6 });
    rows.forEach((r) => io.observe(r));
    return { destroy: () => io.disconnect() };
  }

  function init(scope) {
    scope = scope || doc;
    Nav();
    Cursor();
    reveals(scope);
    let heroField = null, endField = null;
    $$('[data-vpg-field]', scope).forEach((el) => {
      if (el._vpg) return;
      el._vpg = 1;
      /* the WebGL glass cubes where the browser can, the 2D field otherwise */
      let f = null;
      try { f = new GLField(el); if (!f.ripple) f = null; } catch (e) { swallow(e); f = null; }
      if (!f) {
        el.querySelectorAll('canvas').forEach((c) => c.remove());
        el.classList.remove('is-gl');
        f = new Field(el);
      } else {
        const gf = f;
        gf.onLost = () => { const ff = new Field(el); gf.ripple = ff.ripple; registry.push({ el, obj: ff }); };
      }
      registry.push({ el, obj: f });
      if (el.getAttribute('data-vpg-field') === 'hero') heroField = f;
      if (el.getAttribute('data-vpg-field') === 'end') endField = f;
    });
    $$('[data-vpg-hero]', scope).forEach((el) => { if (el._vpg) return; el._vpg = 1; registry.push({ el, obj: Hero(el, heroField) }); });
    $$('[data-vpg-chapter]', scope).forEach((el) => { if (el._vpg) return; el._vpg = 1; registry.push({ el, obj: Chapter(el) }); });
    $$('[data-vpg-day]', scope).forEach((el) => { if (el._vpg) return; el._vpg = 1; registry.push({ el, obj: Day(el) }); });
    $$('.vpg-ai', scope).forEach((el) => { if (el._vpg) return; el._vpg = 1; registry.push({ el, obj: AiDemo(el) }); });
    $$('.vpg-gsp', scope).forEach((el) => { if (el._vpg) return; el._vpg = 1; registry.push({ el, obj: GsDemo(el) }); });
    $$('[data-vpg-why]', scope).forEach((el) => { if (el._vpg) return; el._vpg = 1; registry.push({ el, obj: Why(el) }); });
    const done = $('[data-vpg-done]', scope);
    if (done && endField) {
      setTimeout(() => { const r = done.getBoundingClientRect(); endField.ripple(r.left + 24, r.top + r.height / 2, true); }, 700);
    }
    if (Nav.refresh) Nav.refresh();
  }
  function teardown(scope) {
    registry = registry.filter((r) => {
      if (scope.contains(r.el)) { if (r.obj && r.obj.destroy) r.obj.destroy(); return false; }
      return true;
    });
  }
  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', () => init(doc));
  else init(doc);
  doc.addEventListener('shopify:section:load', (e) => init(e.target));
  doc.addEventListener('shopify:section:unload', (e) => teardown(e.target));
})();
